import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import styles from './CreateOrderModal.module.css';
import ProductSearchInput from './ProductSearchInput';
import ClientSearchInput from './ClientSearchInput';
import PaymentConditionSelect from './PaymentConditionSelect';
import CustomSelect from '../../../../components/CustomSelect/CustomSelect';
import PersonIcon from '@mui/icons-material/Person';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AssignmentIcon from '@mui/icons-material/Assignment';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import InventoryIcon from '@mui/icons-material/Inventory';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import NotesIcon from '@mui/icons-material/Notes';
import api from '../../../../services/apiConfig';
import { useAuthStore } from '../../../../stores/authStore';
import { useFilterStore } from '../../../../stores/filterStore';

interface CreateOrderModalProps {
    onClose: () => void;
    onOrderCreated?: (orderId: number) => void;
}

interface OrderItem {
    id: number;
    vendaId: string;
    produto_id: number;
    referencia: string;
    descricao: string;
    linha: string;
    tabela: string;
    comissao: number;
    com_imb: number;
    unidade: string;
    cod_pcp: string;
    opcionais: string;
    restricoes: string;
    tabela_lib: string;
    base_cost: number;
    option_cost: number;
    preco_imbiseg: number | null;
    mc_rate: number;
    preco: number;
    quantidade: number;
    valor_total: number;
    sizes: { [key: number]: number };
    imbiseg_pricelist?: any[];
    grupo2?: string;
    ca?: string;
    img?: string;
}

interface OrderOption {
    ID: number;
    OPCIONAL: string;
    CUSTO: number;
}

interface TabelaOption {
    ID: number;
    TABELA: string;
    COM: number;
}

interface ClientData {
    ID: number;
    NOME: string;
    DOCUMENTO: string;
    IE: string;
    ENDERECO: string;
    NUMERO: string;
    BAIRRO: string;
    CIDADE: string;
    ESTADO: string;
    CEP: string;
    TELEFONE: string;
    EMAIL: string;
    VENDEDOR_NOME?: string;
    SEGMENTO_NOME?: string;
    COMPLEMENTO?: string;
    TIPO_FRETE?: string;
    TRANSPORTADORA?: string;
    REDESPACHO?: string;
    [key: string]: unknown;
}

interface FiscalOperation {
    ID: number;
    OPERACAO: number;
    DESCRICAO: string;
    FINANCEIRO: string;
    ICMS_FATOR: number;
    PIS_COFINS: number;
    CONT_PREV: number;
}

const CreateOrderModal: React.FC<CreateOrderModalProps> = ({ onClose, onOrderCreated }) => {
    // --- STEP CONTROL ---
    const [step, setStep] = useState<number>(1);
    const [activeTab, setActiveTab] = useState<string>('ENTREGA');
    const [reviewTab, setReviewTab] = useState<string>('ITENS');
    const [showItemModal, setShowItemModal] = useState(false);
    const [editingItem, setEditingItem] = useState<OrderItem | null>(null);

    // --- DATA STATE ---
    const [formData, setFormData] = useState({
        OPERACAO: '',
        COND_PAGAMENTO: '',
        COND_PAGAMENTO_ID: '',
        PRAZO_MEDIO: '',
        PRAZO: '',
        EMISSAO: new Date().toISOString().split('T')[0],
        PREVISAO: '',
        ORDEM_COMPRA: '',
        OBS: '',
        CONTATO: '',
        OBSERVACOES_PCP: '',
        OBSERVACOES_FATURAMENTO: ''
    });

    const [selectedClient, setSelectedClient] = useState<ClientData | null>(null);
    const [taxInfo, setTaxInfo] = useState<{ icms: number, frete: number, estado: string, tipoCalculo: string, segmentoId?: number } | null>(null);
    const [pddTax, setPddTax] = useState<number>(0);
    const [financialTax, setFinancialTax] = useState<number>(0);
    const [fiscalOperations, setFiscalOperations] = useState<FiscalOperation[]>([]);
    const [selectedFiscalOp, setSelectedFiscalOp] = useState<FiscalOperation | null>(null);
    const [freightTypes, setFreightTypes] = useState<{ ID: number, TIPO_FRETE: string }[]>([]);
    const [transporters, setTransporters] = useState<{ ID: number, NOME: string }[]>([]);
    const [optionsList, setOptionsList] = useState<OrderOption[]>([]);
    const [tabelasList, setTabelasList] = useState<TabelaOption[]>([]);

    const [items, setItems] = useState<OrderItem[]>([]);

    const [saving, setSaving] = useState<boolean>(false);
    const [errorAlert, setErrorAlert] = useState<string | null>(null);
    const [successOrderId, setSuccessOrderId] = useState<string | number | null>(null);
    const { role } = useAuthStore();
    
    // --- NOVO: Estado para Simulação de Meta ---
    const [goalSimulation, setGoalSimulation] = useState<{
        target: number;
        current: number;
        scoreFinal: number;   // score real do backend (já ponderado com margem/pesos)
        marginGoal: number;   // meta de margem do período
        currentMargin: number; // margem realizada no período
    } | null>(null);

    useEffect(() => {
        const fetchDashboardStats = async () => {
            try {
                // Se for admin, a simulação não faz sentido (ele vê de todos).
                if (role === 'admin') return;

                const { initialDate, finalDate, selectedCarteiraId } = useFilterStore.getState();
                const user = useAuthStore.getState().user;
                const isSupervisor = role === 'supervisor' || role === 'diretor';
                
                let targetBaseId = selectedCarteiraId;
                if (!isSupervisor) {
                    targetBaseId = user?.base?.toString();
                }

                const dataI = initialDate;
                const dataF = finalDate;
                
                let goalsRes;
                if (isSupervisor && !targetBaseId) {
                    goalsRes = await api.get('/dashboard/supervisor-goals', { params: { dataI, dataF } });
                } else {
                    goalsRes = await api.get('/dashboard/goals', { params: { dataI, dataF, carteiraId: targetBaseId } });
                }

                const salesRes = await api.get('/dashboard/sales-data', { 
                    params: { 
                        dataI, 
                        dataF, 
                        carteiraId: targetBaseId || undefined 
                    } 
                });
                
                let target = 0;
                let current = 0;
                let scoreFinal = 0;
                let marginGoal = 0;
                let currentMargin = 0;
                
                if (goalsRes.data && goalsRes.data.goals && goalsRes.data.goals.length > 0) {
                    const g = goalsRes.data.goals[0];
                    target      = Number(g.goal)        || 0;
                    marginGoal  = Number(g.marginGoal)  || 0;
                    scoreFinal  = Number(g.scoreFinal)  || 0;
                }
                
                if (salesRes.data && salesRes.data.salesData) {
                    salesRes.data.salesData.forEach((item: any) => {
                        current       += Number(item.Venda)  || 0;
                        currentMargin += Number(item.Margem) || 0;
                    });
                }

                // Se o backend não devolveu scoreFinal, calcula localmente como fallback
                if (scoreFinal === 0 && target > 0) {
                    if (marginGoal > 0) {
                        const av = Math.min(current / target, 2);
                        const am = Math.min(currentMargin / marginGoal, 2);
                        scoreFinal = (av * 0.5 + am * 0.5) * 100;
                    } else {
                        scoreFinal = Math.min((current / target) * 100, 200);
                    }
                }
                
                if (target > 0) {
                    setGoalSimulation({ target, current, scoreFinal, marginGoal, currentMargin });
                }
            } catch (err) {
                console.error("Erro ao buscar metas para simulação", err);
            }
        };
        fetchDashboardStats();
    }, [role]);

    const [isOpSelectOpen, setIsOpSelectOpen] = useState(false);
    const [opSearchTerm, setOpSearchTerm] = useState('');
    const [opDropdownStyle, setOpDropdownStyle] = useState<React.CSSProperties>({});
    const [opSelectedIndex, setOpSelectedIndex] = useState(-1);
    const opSelectRef = useRef<HTMLDivElement>(null);
    const opListRef = useRef<HTMLDivElement>(null);

    React.useLayoutEffect(() => {
        const updatePosition = () => {
            if (isOpSelectOpen && opSelectRef.current) {
                const rect = opSelectRef.current.getBoundingClientRect();
                const spaceBelow = window.innerHeight - rect.bottom;
                const dropdownHeight = 250; 
                
                setOpDropdownStyle({
                    position: 'fixed',
                    top: spaceBelow < dropdownHeight && rect.top > dropdownHeight ? 'auto' : rect.bottom + 8,
                    bottom: spaceBelow < dropdownHeight && rect.top > dropdownHeight ? window.innerHeight - rect.top + 8 : 'auto',
                    left: rect.left,
                    right: 'auto',
                    width: rect.width,
                    zIndex: 99999
                });
            }
        };

        if (isOpSelectOpen) {
            updatePosition();
            window.addEventListener('scroll', updatePosition, true);
            window.addEventListener('resize', updatePosition);
        }

        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [isOpSelectOpen]);

    const availableSizes = [33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48];

    // --- EFFECTS ---
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                if (showItemModal) {
                    setShowItemModal(false);
                } else {
                    onClose();
                }
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose, showItemModal]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (opSelectRef.current && !opSelectRef.current.contains(event.target as Node)) {
                const target = event.target as Element;
                if (!target.closest(`.${styles.opDropdown}`)) {
                    setIsOpSelectOpen(false);
                }
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Atualizar selected index quando a busca mudar
    useEffect(() => {
        setOpSelectedIndex(-1);
    }, [opSearchTerm, isOpSelectOpen]);

    const opScrollToIndex = (index: number) => {
        if (!opListRef.current) return;
        const item = opListRef.current.children[index] as HTMLElement;
        if (item) {
            item.scrollIntoView({ block: 'nearest' });
        }
    };

    const handleOpKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        const filteredOps = fiscalOperations.filter(op => op.DESCRICAO.toLowerCase().includes(opSearchTerm.toLowerCase()) || String(op.OPERACAO).includes(opSearchTerm));
        
        if (!isOpSelectOpen) {
            if (e.key === 'ArrowDown' || e.key === 'Enter') {
                if (filteredOps.length > 0) setIsOpSelectOpen(true);
            }
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setOpSelectedIndex((prev) => {
                const nextIndex = prev < filteredOps.length - 1 ? prev + 1 : prev;
                opScrollToIndex(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setOpSelectedIndex((prev) => {
                const nextIndex = prev > 0 ? prev - 1 : prev;
                opScrollToIndex(nextIndex);
                return nextIndex;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (opSelectedIndex >= 0 && opSelectedIndex < filteredOps.length) {
                const op = filteredOps[opSelectedIndex];
                setSelectedFiscalOp(op);
                setFormData(prev => ({ ...prev, OPERACAO: op.DESCRICAO }));
                setOpSearchTerm('');
                setIsOpSelectOpen(false);
            }
        } else if (e.key === 'Escape') {
            setIsOpSelectOpen(false);
        }
    };

    // --- SUBMITS E OUTROS HANDLERS ---

    useEffect(() => {
        const fetchData = async () => {
            // Helper to fetch safely
            const safeGet = async (url: string) => {
                try {
                    const res = await api.get(url);
                    return res.data;
                } catch (err) {
                    console.error(`Failed to fetch ${url}:`, err);
                    return [];
                }
            };

            const [options, tabelas, fiscalOps, freights, transportersData] = await Promise.all([
                safeGet('/orders/options'),
                safeGet('/orders/tabelas'),
                safeGet('/orders/fiscal-operations'),
                safeGet('/orders/freight-types'),
                safeGet('/orders/transporters')
            ]);

            setOptionsList(options);
            setTabelasList(tabelas);
            setFiscalOperations(fiscalOps);
            setFreightTypes(freights);
            setTransporters(transportersData);
        };
        fetchData();
    }, []);

    // --- CALCULATION ENGINE ---
    const calculateFinalSellingPrice = (baseCost: number, optionCost: number, mcRate: number, comissao: number, linha?: string, precoImbiseg?: number | null, overrideFin?: number, overridePdd?: number, qty: number = 1, pricelist?: any[], com_imb?: number, grupo2?: string, overrideFreightType?: string) => {
        console.log(">> Inside calculateSellingPrice");

        const icms = Number(taxInfo?.icms) || 0;
        const pisCofins = Number(selectedFiscalOp?.PIS_COFINS) || 0;
        const cprb = Number(selectedFiscalOp?.CONT_PREV) || 0;
        const frete = Number(taxInfo?.frete) || 0;
        const segmentoId = taxInfo?.segmentoId;

        // Logic Branch: IMBISEG (GRUPO2) vs Standard
        // REGRA: Se o cliente for Exportação (SEGMENTO = 10), o produto IMBISEG deve ser calculado pelo Markup Standard.
        const isExportClient = segmentoId === 10;
        const isImbisegProduct = ((grupo2 || '').toUpperCase() === 'IMBISEG' || (linha || '').toUpperCase().includes('IMBISEG')) && !isExportClient;

        const pdd = isImbisegProduct ? 0 : (overridePdd !== undefined ? overridePdd : (Number(pddTax) || 0));
        const fin = overrideFin !== undefined ? overrideFin : (Number(financialTax) || 0);
        const mc = Number(mcRate) || 0;
        let com = Number(comissao) || 0;

        console.log(`- Calculation Params: base=${baseCost}, opt=${optionCost}, mc=${mc}, com=${com}, com_imb=${com_imb}, grupo2=${grupo2}`);



        if (isImbisegProduct) {
            console.log(">> CALCULO IMBISEG DETECTADO (NOVA FORMULA)");
            let precoBase = 0;

            // Encontrar o preço base na tabela precos_imbseg usando a Faixa (com_imb) e Quantidade
            // IMPORTANTE: Se qty=0 (grade não preenchida ainda), usar 1 para o lookup de tier
            const lookupQty = qty > 0 ? qty : 1;
            if (pricelist && pricelist.length > 0) {
                const targetFaixaDecimal = (com_imb || 0) / 100;
                console.log(`- IMBISEG Lookup: Faixa Selecionada=${com_imb}%, Decimal=${targetFaixaDecimal}, Qtd=${lookupQty} (raw qty=${qty})`);

                // Filtrar por comissão (faixa) exata ou mais próxima
                const faixaEntries = pricelist.filter(p => Math.abs(parseFloat(String(p.comissao)) - targetFaixaDecimal) < 0.0001);

                if (faixaEntries.length > 0) {
                    // Dentro da faixa, encontrar a linha que corresponde à quantidade
                    const tier = faixaEntries.find(p => lookupQty >= p.qtde_minima && lookupQty <= p.qtde_maxima) || faixaEntries[faixaEntries.length - 1];
                    precoBase = parseFloat(String(tier.preço));
                    console.log(`- Tier Matched: Ref=${tier.referencia}, Price=${precoBase}`);
                } else {
                    // Fallback para a primeira faixa disponível se não encontrar a selecionada
                    precoBase = parseFloat(String(pricelist[0].preço));
                    console.log(`- Fallback: Nenhuma faixa exata encontrada. Usando primeira entrada: ${precoBase}`);
                }
            } else if (precoImbiseg) {
                precoBase = precoImbiseg;
                console.log(`- Fallback: Sem pricelist, usando precoImbiseg do produto: ${precoBase}`);
            }

            // Aplica Regra Regional (Norte): Multiplicação por 1.07 para estados específicos
            // REGRA: Somente se estado estiver na lista E o TIPO_FRETE for diferente de ID 3 (CIF REDESPACHO)
            const northRegionStates = ['RO', 'AC', 'AM', 'RR', 'PA', 'AP', 'TO'];
            const currentState = taxInfo?.estado || '';
            const currentFreight = freightTypes.find(ft => ft.TIPO_FRETE === (overrideFreightType || selectedClient?.TIPO_FRETE));
            const freightTypeId = currentFreight?.ID;

            if (northRegionStates.includes(currentState.toUpperCase()) && freightTypeId !== 3) {
                precoBase = precoBase * 1.07;
                console.log(`- IMBISEG Regional Adjust: State=${currentState}, FreightID=${freightTypeId}, New Base=${precoBase.toFixed(4)}`);
            }

            // Nova Fórmula IMBISEG: Preço Base * (1 + Taxa Financeira/100) + Opcionais
            // TAXA FINANCEIRA (fin) = (PRAZO MEDIO - PRAZO PADRAO) * (TAXA / 30)
            let finalPrice = (precoBase * (1 + (fin / 100))) + optionCost;

            // Multiplicador Composto (Kadesh): R3 é o Padrão (COM=3). 
            // Cada nível acima de R3 multiplica por 1.03. Cada nível abaixo divide por 1.03.
            const targetCom = 3;
            const comDiff = Math.round(com) - targetCom;
            if (comDiff > 0) {
                console.log(`- IMBISEG: Subindo ${comDiff} níveis acima de R3 (* 1.03)`);
                for (let i = 0; i < comDiff; i++) finalPrice = finalPrice * 1.03;
            } else if (comDiff < 0) {
                console.log(`- IMBISEG: Descendo ${Math.abs(comDiff)} níveis abaixo de R3 (/ 1.03)`);
                for (let i = 0; i < Math.abs(comDiff); i++) finalPrice = finalPrice / 1.03;
            }

            // FPRECO2 sugerido no logado (opcional, apenas log)
            // console.log("- FPRECO2 (Teto +3%):", (finalPrice * 1.03).toFixed(2));

            finalPrice = parseFloat(finalPrice.toFixed(2));

            console.log(`- Resultado Final (NOVA FORMULA c/ COMPOSTO UNIV.): ${precoBase} * (1 + ${fin.toFixed(4)}/100) + ${optionCost} (diff R3: ${comDiff}) = ${finalPrice}`);
            return isNaN(finalPrice) ? 0 : finalPrice;
        }

        // --- STANDARD KADESH CALCULATION ---
        // FCOM1 := min(3, comissao)
        const fCom1 = com > 3 ? 3 : com;

        // Fmarkup := 1 / (1 - (SUM(deductions) / 100))
        const totalDeductions = icms + pisCofins + cprb + fCom1 + frete + fin + mc + pdd;
        const divisor = 1 - (totalDeductions / 100);
        const fMarkup = divisor > 0 ? 1 / divisor : 0;

        let price = 0;
        if (fMarkup > 0) {
            price = (baseCost + optionCost) * fMarkup;
        }

        // Compound Commission Logic for Kadesh
        if (comissao > 3) {
            const extraSteps = Math.round(comissao) - 3;
            for (let i = 0; i < extraSteps; i++) {
                price = price * 1.03;
            }
        }

        return isNaN(price) ? 0 : price;
    };

    const renderBreakdown = (item: OrderItem) => {
        const price = item.preco || 0;
        const qty = item.quantidade || 0;
        const icmsRate = Number(taxInfo?.icms) || 0;
        const pisCofinsRate = Number(selectedFiscalOp?.PIS_COFINS) || 0;
        const cprbRate = Number(selectedFiscalOp?.CONT_PREV) || 0;
        const isExportClient = taxInfo?.segmentoId === 10;
        const isImbiseg = ((item.grupo2 || '').toUpperCase() === 'IMBISEG' || (item.linha || '').toUpperCase().includes('IMBISEG'));
        const isImbisegExport = isImbiseg && isExportClient;
        const pddRate = isImbisegExport ? 0 : (Number(pddTax) || 0);
        const finRate = Number(financialTax) || 0;
        const freteRate = Number(taxInfo?.frete) || 0;
        const mcRate = Number(item.mc_rate) || 0;
        const comRate = Number(item.comissao) || 0;
        const fCom1 = isImbisegExport ? 0 : comRate;

        // R$ Values (Total para a Quantidade)
        const vCost = (item.base_cost + item.option_cost) * qty;
        const vIcms = (price * qty) * (icmsRate / 100);
        const vPisCofins = (price * qty) * (pisCofinsRate / 100);
        const vCprb = (price * qty) * (cprbRate / 100);
        const vImpostos = vIcms + vPisCofins + vCprb;

        const vPdd = (price * qty) * (pddRate / 100);
        const vFin = (price * qty) * (finRate / 100);
        const vFrete = (price * qty) * (freteRate / 100);
        const vComissao = (price * qty) * (fCom1 / 100);

        const vDespesas = vPdd + vFin + vFrete + vComissao;

        const vMargemR$ = (price * qty) - vCost - vImpostos - vDespesas;
        const pMargem = (price * qty) > 0 ? (vMargemR$ / (price * qty)) * 100 : 0;

        const formatR$ = (val: number) => val.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        const formatP = (val: number) => val.toFixed(2);

        return (
            <div className={styles.breakdownContainer}>
                {role === 'admin' ? (
                    <h4 className={styles.breakdownTitle}>Detalhamento do Cálculo (Total: {qty} pares)</h4>
                ) : (
                    <h4 className={styles.breakdownTitle}>Margens (Total: {qty} pares)</h4>
                )}

                <div className={styles.breakdownGrid}>
                    {role === 'admin' && (
                        <>
                            {/* Custo */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownLabel}>Custo Total</div>
                                <div className={styles.breakdownValueBox}>{formatR$(vCost)}</div>
                            </div>
                            {/* ICMS */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownHorizontalGroup}>
                                    <div style={{ flex: '0 0 60px' }}>
                                        <div className={styles.breakdownLabel}>% ICMS</div>
                                        <div className={styles.breakdownValueBox}>{formatP(icmsRate)}</div>
                                    </div>
                                    <div>
                                        <div className={styles.breakdownLabel}>R$ ICMS</div>
                                        <div className={styles.breakdownValueBox}>{formatR$(vIcms)}</div>
                                    </div>
                                </div>
                            </div>
                            {/* PIS/COFINS */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownHorizontalGroup}>
                                    <div style={{ flex: '0 0 60px' }}>
                                        <div className={styles.breakdownLabel}>% PIS/COF</div>
                                        <div className={styles.breakdownValueBox}>{formatP(pisCofinsRate)}</div>
                                    </div>
                                    <div>
                                        <div className={styles.breakdownLabel}>R$ PIS/COF</div>
                                        <div className={styles.breakdownValueBox}>{formatR$(vPisCofins)}</div>
                                    </div>
                                </div>
                            </div>
                            {/* CPRB */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownHorizontalGroup}>
                                    <div style={{ flex: '0 0 60px' }}>
                                        <div className={styles.breakdownLabel}>% CPRB</div>
                                        <div className={styles.breakdownValueBox}>{formatP(cprbRate)}</div>
                                    </div>
                                    <div>
                                        <div className={styles.breakdownLabel}>R$ CPRB</div>
                                        <div className={styles.breakdownValueBox}>{formatR$(vCprb)}</div>
                                    </div>
                                </div>
                            </div>
                            {/* Total Impostos */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownLabel}>$ Impostos</div>
                                <div className={`${styles.breakdownValueBox} ${styles.warningValue}`} >{formatR$(vImpostos)}</div>
                            </div>

                            <div className={styles.breakdownDivider}></div>

                            {/* PDD */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownHorizontalGroup}>
                                    <div style={{ flex: '0 0 60px' }}>
                                        <div className={styles.breakdownLabel}>% PDD</div>
                                        <div className={styles.breakdownValueBox}>{formatP(pddRate)}</div>
                                    </div>
                                    <div>
                                        <div className={styles.breakdownLabel}>R$ PDD</div>
                                        <div className={styles.breakdownValueBox}>{formatR$(vPdd)}</div>
                                    </div>
                                </div>
                            </div>
                            {/* Financeiro */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownHorizontalGroup}>
                                    <div style={{ flex: '0 0 60px' }}>
                                        <div className={styles.breakdownLabel}>% Finan.</div>
                                        <div className={styles.breakdownValueBox}>{formatP(finRate)}</div>
                                    </div>
                                    <div>
                                        <div className={styles.breakdownLabel}>R$ Finan.</div>
                                        <div className={styles.breakdownValueBox}>{formatR$(vFin)}</div>
                                    </div>
                                </div>
                            </div>
                            {/* Frete */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownHorizontalGroup}>
                                    <div style={{ flex: '0 0 60px' }}>
                                        <div className={styles.breakdownLabel}>% Frete</div>
                                        <div className={styles.breakdownValueBox}>{formatP(freteRate)}</div>
                                    </div>
                                    <div>
                                        <div className={styles.breakdownLabel}>R$ Frete</div>
                                        <div className={styles.breakdownValueBox}>{formatR$(vFrete)}</div>
                                    </div>
                                </div>
                            </div>
                            {/* Comissão */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownHorizontalGroup}>
                                    <div style={{ flex: '0 0 60px' }}>
                                        <div className={styles.breakdownLabel}>% Comis.</div>
                                        <div className={styles.breakdownValueBox}>{formatP(fCom1)}</div>
                                    </div>
                                    <div>
                                        <div className={styles.breakdownLabel}>R$ Comis.</div>
                                        <div className={styles.breakdownValueBox}>{formatR$(vComissao)}</div>
                                    </div>
                                </div>
                            </div>
                            {/* Total Despesas */}
                            <div className={styles.breakdownGroup}>
                                <div className={styles.breakdownLabel}>R$ Despesas</div>
                                <div className={`${styles.breakdownValueBox} ${styles.warningValue}`} >{formatR$(vDespesas)}</div>
                            </div>

                            <div className={styles.breakdownDivider}></div>
                        </>
                    )}

                    {/* Margem R$ */}
                    <div className={styles.breakdownGroup}>
                        <div className={styles.breakdownLabel}>Margem R$</div>
                        <div className={`${styles.breakdownValueBox} ${styles.highlightValue}`} style={{ backgroundColor: '#f0f9ff', borderColor: '#bae6fd' }}>{formatR$(vMargemR$)}</div>
                    </div>
                    {/* Margem % */}
                    <div className={styles.breakdownGroup}>
                        <div className={styles.breakdownLabel}>% Margem Atual</div>
                        <div className={`${styles.breakdownValueBox} ${pMargem < mcRate ? styles.warningValue : styles.successValue}`} style={{ fontWeight: 800, fontSize: '1.1rem' }}>{formatP(pMargem)}%</div>
                    </div>

                </div>
            </div>
        );
    };

    // --- HANDLERS (HEADER) ---
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;

        if (name === 'OPERACAO') {
            const op = fiscalOperations.find(f => f.DESCRICAO === value);
            setSelectedFiscalOp(op || null);
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleClientSelect = async (client: any) => {
        // Resolve o TIPO_FRETE correto: client pode trazer número (ID) ou string
        const matchedFreight = freightTypes.find(ft =>
            ft.ID === client.TIPO_FRETE ||
            ft.TIPO_FRETE === client.TIPO_FRETE
        );

        // Busca o padrão "CIF Destino" (ou qualquer opção com "CIF Destino" no nome, depois só "CIF")
        const cifDestinoFreight =
            freightTypes.find(ft => ft.TIPO_FRETE?.toLowerCase().includes('cif destino')) ||
            freightTypes.find(ft => ft.TIPO_FRETE?.toLowerCase().includes('cif')) ||
            freightTypes[0];

        const resolvedFreight = matchedFreight?.TIPO_FRETE || cifDestinoFreight?.TIPO_FRETE || '';

        const clientWithFreight = {
            ...client,
            TIPO_FRETE: resolvedFreight
        };

        setSelectedClient(clientWithFreight);
        setFormData(prev => ({
            ...prev
        }));

        if (client && client.ID) {
            try {
                const response = await api.get(`/orders/client-tax-info/${client.ID}`);
                console.log("Tax Info Received:", response.data);
                setTaxInfo(response.data);
            } catch (error: any) {
                console.error("Erro ao buscar dados fiscais do cliente:", error);
                alert(`Erro ao carregar dados fiscais: ${error.message}`);
            }
        }
    };

    const handleClientDataChange = (field: keyof ClientData, value: string) => {
        if (!selectedClient) return;
        setSelectedClient({
            ...selectedClient,
            [field]: value
        });

        // Trigger automatic recalculation of all items if freight type changes
        if (field === 'TIPO_FRETE') {
            const newFreightType = value;
            setItems(prevItems => prevItems.map(item => {
                const newPrice = calculateFinalSellingPrice(
                    item.base_cost,
                    item.option_cost,
                    item.mc_rate,
                    item.comissao,
                    item.linha,
                    item.preco_imbiseg,
                    financialTax,
                    pddTax,
                    item.quantidade,
                    item.imbiseg_pricelist,
                    item.com_imb,
                    item.grupo2,
                    newFreightType
                );
                return {
                    ...item,
                    preco: newPrice,
                    valor_total: item.quantidade * (Number(newPrice) || 0)
                };
            }));
        }
    };

    const handlePaymentSelect = (condition: any) => {
        setFormData(prev => ({
            ...prev,
            COND_PAGAMENTO: condition.DESCRICAO,
            COND_PAGAMENTO_ID: String(condition.ID),
            PRAZO_MEDIO: String(condition.PRAZO_MEDIO)
        }));
        setPddTax(condition.PDD || 0);

        const prazoMedio = Number(condition.PRAZO_MEDIO) || 0;
        const prazoPadrao = Number(condition.PRAZO_PADRAO) || 0;
        const taxa = Number(condition.TAXA) || 0;

        // TAXA FINANCEIRA = (PRAZO MEDIO - PRAZO PADRÃO) * (TAXA / 30)
        let finTax = ((prazoMedio - prazoPadrao) * (taxa / 30));
        if (isNaN(finTax)) finTax = 0;
        setFinancialTax(finTax);

        // Recalculate ALL items when payment/financial tax changes
        setItems(prev => prev.map(item => {
            const newPrice = calculateFinalSellingPrice(item.base_cost, item.option_cost, item.mc_rate, item.comissao, item.linha, item.preco_imbiseg, finTax, condition.PDD || 0, item.quantidade, item.imbiseg_pricelist, item.com_imb, item.grupo2);
            const total = item.quantidade * newPrice;

            return {
                ...item,
                preco: newPrice,
                valor_total: total
            };
        }));
    };

    // --- MODAL HANDLERS ---
    const handleOpenItemModal = () => {
        // Start a clean item
        const initialSizes: { [key: number]: number } = {};
        availableSizes.forEach(s => initialSizes[s] = 0);

        const isAmostraGratis = formData.OPERACAO?.toUpperCase().includes('AMOSTRA GR');

        setEditingItemId(null);
        setEditingItem({
            id: Date.now(),
            vendaId: '',
            produto_id: 0,
            referencia: '',
            descricao: '',
            linha: '',
            tabela: '',
            comissao: 0,
            com_imb: 0,
            unidade: isAmostraGratis ? 'Pé' : 'Pares',
            cod_pcp: '',
            opcionais: '',
            restricoes: '',
            tabela_lib: '',
            base_cost: 0,
            option_cost: 0,
            preco_imbiseg: null,
            mc_rate: 0,
            preco: 0,
            quantidade: 0,
            valor_total: 0,
            sizes: initialSizes,
            grupo2: ''
        });
        setShowItemModal(true);
    };

    const handleProductSelectInModal = async (product: { ID?: number, REFERENCIA: string, DESCRICAO: string, COD_PCP?: string, LINHA?: string, GRUPO2?: string, CUSTO?: number, MC?: number, PRECO_IMBISEG?: number | null }) => {
        if (!editingItem) return;

        let pricelist: any[] = [];
        const isImbiseg = product.GRUPO2?.toUpperCase() === 'IMBISEG' || product.LINHA?.toUpperCase().includes('IMBISEG');

        if (isImbiseg) {
            try {
                const res = await api.get(`/products/imbiseg-prices/${product.REFERENCIA}`);
                pricelist = res.data;
            } catch (err) {
                console.error("Erro ao carregar tabela IMBISEG:", err);
            }
        }

        const baseCost = product.CUSTO ? parseFloat(String(product.CUSTO)) : 0;
        const mcRate = product.MC ? parseFloat(String(product.MC)) : 0;
        const linha = product.LINHA || '';
        const grupo2 = product.GRUPO2 || '';
        const precoImbiseg = product.PRECO_IMBISEG ? parseFloat(String(product.PRECO_IMBISEG)) : null;

        let ca = '';
        let img = '';
        try {
            const extraRes = await api.get(`/products/details/${encodeURIComponent(product.REFERENCIA)}`);
            if (extraRes.data) {
                ca = extraRes.data.CA || '';
                img = extraRes.data.img || '';
            }
        } catch(e) {
            console.error("Erro ao carregar detalhes extras (CA/Imagem):", e);
        }

        // Find default Table: Try ID 1, fallback to first available
        const defaultTable = tabelasList.find(t => t.ID === 1) || tabelasList[0];
        const initialComissao = defaultTable ? Number(defaultTable.COM) : 0;
        const initialTabelaName = defaultTable ? defaultTable.TABELA : '';

        // Calculate Price
        // com_imb = faixa selector for IMBISEG price tier (0.5, 1.0, 1.5) — NOT the table's COM
        let initialComImb = 0;
        if (isImbiseg && pricelist.length > 0) {
            // Default to the smallest faixa available (e.g., 0.5%)
            const uniqueFaixas = Array.from(new Set(pricelist.map(p => parseFloat(String(p.comissao)) * 100)));
            uniqueFaixas.sort((a, b) => a - b);
            initialComImb = uniqueFaixas[0] || 0;
            console.log(`- Initial Faixa IMBISEG: ${initialComImb}% (from pricelist)`);
        }
        const price = calculateFinalSellingPrice(baseCost, 0, mcRate, initialComissao, linha, precoImbiseg, undefined, undefined, 1, pricelist, initialComImb, grupo2);

        setEditingItem({
            ...editingItem,
            produto_id: product.ID || 0,
            referencia: product.REFERENCIA,
            descricao: product.DESCRICAO,
            linha: linha,
            grupo2: grupo2,
            cod_pcp: product.COD_PCP || '',
            base_cost: baseCost,
            mc_rate: mcRate,
            preco_imbiseg: precoImbiseg,
            imbiseg_pricelist: pricelist,
            tabela: initialTabelaName,
            comissao: initialComissao,
            com_imb: initialComImb,
            preco: price,
            ca: ca,
            img: img
        });
    };

    const handleEditingItemChange = (field: keyof OrderItem, value: any) => {
        if (!editingItem) return;

        let newItem = { ...editingItem, [field]: value };

        // Logic triggers
        if (field === 'opcionais') {
            const selectedOption = optionsList.find(opt => opt.OPCIONAL === value);
            const newOptCost = selectedOption ? parseFloat(String(selectedOption.CUSTO)) : 0;
            newItem.option_cost = newOptCost;
            newItem.preco = calculateFinalSellingPrice(newItem.base_cost, newOptCost, newItem.mc_rate, newItem.comissao, newItem.linha, newItem.preco_imbiseg, undefined, undefined, newItem.quantidade, newItem.imbiseg_pricelist, newItem.com_imb, newItem.grupo2);
        }

        if (field === 'tabela') {
            const selectedTable = tabelasList.find(t => t.TABELA === value);
            if (selectedTable) {
                // UPDATE commission from tabelas COM — but do NOT touch com_imb (faixa selector)
                const newCom = selectedTable.COM || 0;
                newItem.comissao = newCom;
                newItem.preco = calculateFinalSellingPrice(newItem.base_cost, newItem.option_cost, newItem.mc_rate, newCom, newItem.linha, newItem.preco_imbiseg, undefined, undefined, newItem.quantidade, newItem.imbiseg_pricelist, newItem.com_imb, newItem.grupo2);
            }
        }

        if (field === 'comissao') {
            const val = parseFloat(String(value)) || 0;
            newItem.comissao = val;
            // Do NOT overwrite com_imb — comissao is the real commission, com_imb is the price tier selector
            newItem.preco = calculateFinalSellingPrice(newItem.base_cost, newItem.option_cost, newItem.mc_rate, val, newItem.linha, newItem.preco_imbiseg, undefined, undefined, newItem.quantidade, newItem.imbiseg_pricelist, newItem.com_imb, newItem.grupo2);
        }

        if (field === 'com_imb') {
            const val = parseFloat(String(value)) || 0;
            newItem.com_imb = val;
            newItem.preco = calculateFinalSellingPrice(newItem.base_cost, newItem.option_cost, newItem.mc_rate, newItem.comissao, newItem.linha, newItem.preco_imbiseg, undefined, undefined, newItem.quantidade, newItem.imbiseg_pricelist, val, newItem.grupo2);
        }

        if (field === 'quantidade') {
            const val = parseFloat(String(value)) || 0;
            newItem.quantidade = val;
            newItem.preco = calculateFinalSellingPrice(newItem.base_cost, newItem.option_cost, newItem.mc_rate, newItem.comissao, newItem.linha, newItem.preco_imbiseg, undefined, undefined, val, newItem.imbiseg_pricelist, newItem.com_imb, newItem.grupo2);
        }

        if (field === 'preco') {
            const manualPrice = parseFloat(String(value)) || 0;
            const targetPrice = calculateFinalSellingPrice(newItem.base_cost, newItem.option_cost, newItem.mc_rate, newItem.comissao, newItem.linha, newItem.preco_imbiseg, undefined, undefined, newItem.quantidade, newItem.imbiseg_pricelist, newItem.com_imb, newItem.grupo2);

            // Check margin
            if (manualPrice < targetPrice - 0.05) {
                // Calcular a margem real gerada pelo novo preço
                const icmsRate = Number(taxInfo?.icms) || 0;
                const pisCofinsRate = Number(selectedFiscalOp?.PIS_COFINS) || 0;
                const cprbRate = Number(selectedFiscalOp?.CONT_PREV) || 0;
                const isExportClient = taxInfo?.segmentoId === 10;
                const isImbisegExport = ((newItem.grupo2 || '').toUpperCase() === 'IMBISEG' || (newItem.linha || '').toUpperCase().includes('IMBISEG')) && isExportClient;
                const pddRate = isImbisegExport ? 0 : (Number(pddTax) || 0);
                const finRate = Number(financialTax) || 0;
                const freteRate = Number(taxInfo?.frete) || 0;
                const fCom1 = isImbisegExport ? 0 : (Number(newItem.comissao) || 0);

                const vCostUnit = newItem.base_cost + newItem.option_cost;
                const vImpostosUnit = manualPrice * ((icmsRate + pisCofinsRate + cprbRate) / 100);
                const vDespesasUnit = manualPrice * ((pddRate + finRate + freteRate + fCom1) / 100);
                const vMargemUnit = manualPrice - vCostUnit - vImpostosUnit - vDespesasUnit;
                const pMargem = manualPrice > 0 ? (vMargemUnit / manualPrice) * 100 : 0;

                const diffPercent = (newItem.mc_rate || 0) - pMargem;
                const msg = `Margem Baixa (-${diffPercent.toFixed(2)}%)`;
                if (!newItem.restricoes.includes('Margem Baixa')) {
                    newItem.restricoes = newItem.restricoes ? `${newItem.restricoes}; ${msg}` : msg;
                } else {
                    newItem.restricoes = newItem.restricoes.replace(/Margem Baixa \(.*\)/, msg);
                }
            } else {
                newItem.restricoes = newItem.restricoes.replace(/;? ?Margem Baixa \(.*\)/, '').trim();
                if (newItem.restricoes.startsWith(';')) newItem.restricoes = newItem.restricoes.substring(1).trim();
            }
        }

        // Update Total based on new price and quantity
        const total = newItem.quantidade * (Number(newItem.preco) || 0);
        newItem.valor_total = total;

        if (field !== 'preco') {
            newItem.restricoes = newItem.restricoes.replace(/;? ?Margem Baixa \(.*\)/, '').trim();
            if (newItem.restricoes.startsWith(';')) newItem.restricoes = newItem.restricoes.substring(1).trim();
        }

        setEditingItem(newItem);
    };

    // Restore handleItemFieldChange for the main list
    const handleItemFieldChange = (id: number, field: keyof OrderItem, value: any) => {
        setItems(items.map(item => {
            if (item.id === id) {
                let newItem = { ...item, [field]: value };

                // Logic triggers (Replicated from handleEditingItemChange)
                if (field === 'opcionais') {
                    const selectedOption = optionsList.find(opt => opt.OPCIONAL === value);
                    const newOptCost = selectedOption ? parseFloat(String(selectedOption.CUSTO)) : 0;
                    newItem.option_cost = newOptCost;
                    newItem.preco = calculateFinalSellingPrice(newItem.base_cost, newOptCost, newItem.mc_rate, newItem.comissao, newItem.linha, newItem.preco_imbiseg, undefined, undefined, newItem.quantidade, newItem.imbiseg_pricelist, newItem.com_imb, newItem.grupo2);
                }

                if (field === 'tabela') {
                    const selectedTable = tabelasList.find(t => t.TABELA === value);
                    if (selectedTable) {
                        const newCom = selectedTable.COM || 0;
                        newItem.comissao = newCom;
                        // Do NOT touch com_imb — it's the faixa selector, independent from tabela
                        newItem.preco = calculateFinalSellingPrice(newItem.base_cost, newItem.option_cost, newItem.mc_rate, newCom, newItem.linha, newItem.preco_imbiseg, undefined, undefined, newItem.quantidade, newItem.imbiseg_pricelist, newItem.com_imb, newItem.grupo2);
                    }
                }

                if (field === 'comissao') {
                    const val = parseFloat(String(value)) || 0;
                    newItem.comissao = val;
                    newItem.preco = calculateFinalSellingPrice(newItem.base_cost, newItem.option_cost, newItem.mc_rate, val, newItem.linha, newItem.preco_imbiseg, undefined, undefined, newItem.quantidade, newItem.imbiseg_pricelist, newItem.com_imb, newItem.grupo2);
                }

                if (field === 'preco') {
                    const manualPrice = parseFloat(String(value)) || 0;
                    const targetPrice = calculateFinalSellingPrice(newItem.base_cost, newItem.option_cost, newItem.mc_rate, newItem.comissao, newItem.linha, newItem.preco_imbiseg, undefined, undefined, newItem.quantidade, newItem.imbiseg_pricelist, newItem.com_imb, newItem.grupo2);

                    if (manualPrice < targetPrice - 0.05) {
                        const diff = targetPrice - manualPrice;
                        const diffPercent = targetPrice > 0 ? (diff / targetPrice) * 100 : 0;
                        const msg = `Margem Baixa (-${diffPercent.toFixed(2)}%)`;
                        if (!newItem.restricoes.includes('Margem Baixa')) {
                            newItem.restricoes = newItem.restricoes ? `${newItem.restricoes}; ${msg}` : msg;
                        } else {
                            newItem.restricoes = newItem.restricoes.replace(/Margem Baixa \(.*\)/, msg);
                        }
                    } else {
                        newItem.restricoes = newItem.restricoes.replace(/;? ?Margem Baixa \(.*\)/, '').trim();
                        if (newItem.restricoes.startsWith(';')) newItem.restricoes = newItem.restricoes.substring(1).trim();
                    }
                }

                const total = newItem.quantidade * (Number(newItem.preco) || 0);
                newItem.valor_total = total;

                return newItem;
            }
            return item;
        }));
    };

    const handleSizeChangeInModal = (size: number, qty: number) => {
        if (!editingItem) return;

        const isAmostraGratis = formData.OPERACAO?.toUpperCase().includes('AMOSTRA GR');

        const newSizes = { ...editingItem.sizes, [size]: qty };
        const totalQty = Object.values(newSizes).reduce((a, b) => a + Number(b), 0);

        // Removed early validation, wait for confirmation

        // Quantity affects IMBISEG pricelist tier, so we must recalculate price
        const newPrice = calculateFinalSellingPrice(
            editingItem.base_cost,
            editingItem.option_cost,
            editingItem.mc_rate,
            editingItem.comissao,
            editingItem.linha,
            editingItem.preco_imbiseg,
            undefined,
            undefined,
            totalQty,
            editingItem.imbiseg_pricelist,
            editingItem.com_imb,
            editingItem.grupo2
        );

        const totalVal = totalQty * (Number(newPrice) || 0);

        setEditingItem({
            ...editingItem,
            sizes: newSizes,
            quantidade: totalQty,
            preco: newPrice,
            valor_total: totalVal
        });
    };

    const saveItemFromModal = () => {
        // Se não há grades pendentes (e nenhuma em edição), exige produto e quantidade
        const effectivePendingCount = pendingItems.length;
        if (effectivePendingCount === 0) {
            if (!editingItem || !editingItem.referencia) {
                setErrorAlert("Selecione um produto.");
                return;
            }
            if (editingItem.quantidade === 0) {
                setErrorAlert("Informe a quantidade na grade.");
                return;
            }
        }

        const operacao = formData.OPERACAO?.toUpperCase() || '';
        const isAmostraHomologacao = operacao.includes('AMOSTRA') && operacao.includes('HOMOLOGA');
        const isAmostraGratisNormal = operacao.includes('AMOSTRA GR') && !isAmostraHomologacao;

        if (isAmostraHomologacao && (editingItem?.quantidade ?? 0) > 10) {
            setErrorAlert("Permitido apenas 10 pés por produto para Amostra de Homologação.");
            return;
        }

        if (isAmostraGratisNormal && (editingItem?.quantidade ?? 0) > 3) {
            setErrorAlert("Permitido apenas 3 pés por produto para Amostra Grátis.");
            return;
        }

        // Resolve lista final de pendentes: se havia item em edição, substitui pelo atual
        let resolvedPending = [...pendingItems];
        if (editingPendingIdx !== null && editingItem && editingItem.referencia && editingItem.quantidade > 0) {
            resolvedPending = pendingItems.map((item, i) =>
                i === editingPendingIdx ? { ...editingItem, id: item.id } : item
            );
        }

        // Monta lista final: pendentes resolvidos + item atual (se não for edição de pendente e tiver qtd)
        const currentItemToAdd = (editingPendingIdx === null && editingItem && editingItem.quantidade > 0)
            ? [editingItem]
            : [];

        const allToSave: OrderItem[] = [...resolvedPending, ...currentItemToAdd];

        if (allToSave.length === 0) {
            setErrorAlert("Informe a quantidade na grade.");
            return;
        }

        if (editingItemId !== null) {
            // Modo edição: substitui o item existente pelos novos (1 ou mais)
            setItems(prev => {
                const idx = prev.findIndex(i => i.id === editingItemId);
                if (idx === -1) return [...prev, ...allToSave];
                return [
                    ...prev.slice(0, idx),
                    ...allToSave,
                    ...prev.slice(idx + 1)
                ];
            });
        } else {
            setItems(prev => [...prev, ...allToSave]);
        }

        setShowItemModal(false);
        setEditingItem(null);
        setEditingItemId(null);
        setPendingItems([]);
        setEditingPendingIdx(null);
    };



    const handleRemoveItem = (id: number) => {
        setItems(items.filter(item => item.id !== id));
    };

    const [editingItemId, setEditingItemId] = useState<number | null>(null);
    const [pendingItems, setPendingItems] = useState<OrderItem[]>([]);
    const [productSearchKey, setProductSearchKey] = useState<number>(0);
    const [editingPendingIdx, setEditingPendingIdx] = useState<number | null>(null);

    const handleEditItem = (item: OrderItem) => {
        setEditingItemId(item.id);
        setPendingItems([]);
        setEditingItem({ ...item });
        setShowItemModal(true);
    };

    const handleAddGrade = () => {
        if (!editingItem || !editingItem.referencia) {
            setErrorAlert('Selecione um produto antes de adicionar a grade.');
            return;
        }
        if (editingItem.quantidade === 0) {
            setErrorAlert('Informe a quantidade na grade antes de adicionar.');
            return;
        }

        if (editingPendingIdx !== null) {
            // Modo edição: substitui o item no índice correto
            setPendingItems(prev => prev.map((item, i) => i === editingPendingIdx ? { ...editingItem, id: item.id } : item));
            setEditingPendingIdx(null);
        } else {
            // Novo item: empilha na lista pendente
            setPendingItems(prev => [...prev, { ...editingItem, id: Date.now() }]);
        }

        // Reseta a busca de produto (remonta o componente ProductSearchInput)
        setProductSearchKey(prev => prev + 1);

        // Reseta o produto selecionado e a grade, deixando o modal limpo para novo produto
        const resetSizes: { [key: number]: number } = {};
        availableSizes.forEach(s => resetSizes[s] = 0);
        setEditingItem(prev => prev ? {
            ...prev,
            id: Date.now() + 1,
            produto_id: 0,
            referencia: '',
            descricao: '',
            linha: '',
            grupo2: '',
            cod_pcp: '',
            base_cost: 0,
            mc_rate: 0,
            option_cost: 0,
            opcionais: '',
            preco: 0,
            ca: '',
            img: '',
            sizes: resetSizes,
            quantidade: 0,
            valor_total: 0,
            restricoes: ''
        } : prev);
    };

    const handleSaveOrder = async () => {
        if (!selectedClient) { setErrorAlert('Selecione um cliente para vincular ao pedido.'); return; }
        if (items.length === 0) { setErrorAlert('Adicione pelo menos um item antes de salvar o pedido.'); return; }

        try {
            setSaving(true);

            // Preparar dados para envio
            const orderData = {
                // Header do pedido
                cliente: selectedClient.ID,
                operacao: selectedFiscalOp?.OPERACAO || null,
                operacao_fiscal: selectedFiscalOp?.ID || null,
                cond_pagamento: parseInt(formData.COND_PAGAMENTO_ID) || null,
                prazo: formData.PRAZO,
                prazo_medio: formData.PRAZO_MEDIO,
                frete_tipo: freightTypes.find(ft => ft.TIPO_FRETE === selectedClient.TIPO_FRETE)?.ID || null,
                transportadora: null, // Será preenchido pelo backend ou Logística posteriormente
                frete_redesp_tipo: selectedClient.FRETE_REDESP_TIPO,
                redespacho: selectedClient.REDESPACHO,
                redespacho_desc: selectedClient.REDESPACHO_DESC,
                contato: formData.CONTATO,
                tipo_cliente: selectedClient.TIPO_CLIENTE,
                obs: formData.OBS,
                observacoes_pcp: formData.OBSERVACOES_PCP,
                observacoes_faturamento: formData.OBSERVACOES_FATURAMENTO,
                restricao: items.map(item => item.restricoes).filter(r => r).join('; ') || null,
                cod_teorema: null, // Pode ser adicionado um campo no futuro
                oc: formData.ORDEM_COMPRA || null,
                empresa: null, // Será tratado no backend baseado na base

                // Dados de entrega
                entrega_tipo: null,
                entrega_nome: selectedClient.ENTREGA_NOME,
                entrega_cnpj: selectedClient.ENTREGA_CNPJ,
                entrega_inscr: selectedClient.ENTREGA_INSCR,
                entrega_endereco: selectedClient.ENTREGA_ENDERECO,
                entrega_complemento: selectedClient.ENTREGA_COMPLEMENTO,
                entrega_bairro: selectedClient.ENTREGA_BAIRRO,
                entrega_cep: selectedClient.ENTREGA_CEP,
                entrega_cidade: selectedClient.ENTREGA_CIDADE,
                entrega_uf: selectedClient.ENTREGA_UF,

                // Impostos
                icms: taxInfo?.icms || 0,
                pis_cofins: selectedFiscalOp?.PIS_COFINS || 0,
                cprb: selectedFiscalOp?.CONT_PREV || 0,
                pdd: pddTax || 0,
                indice_fin: financialTax || 0,
                media_frete: taxInfo?.frete || 0,
                frete_cotado: 0, // TODO: implementar se necessário

                // Itens do pedido
                items: items.map(item => ({
                    produto_id: item.produto_id,
                    cod_pcp: item.cod_pcp,
                    referencia: item.referencia,
                    descricao: item.descricao,
                    quantidade: item.quantidade,
                    preco: item.preco,
                    base_cost: item.base_cost,
                    comissao: item.comissao,
                    com_imb: item.com_imb,
                    tabela: item.tabela,
                    opcionais: item.opcionais,
                    sizes: item.sizes,
                    unidade: item.unidade
                }))
            };

            console.log("Enviando pedido:", orderData);
            console.log("[DEBUG] Total de itens a enviar:", items.length);
            console.log("[DEBUG] Items:", items);

            // Fazer requisição para o backend
            const response = await api.post('/orders', orderData);

            console.log("Pedido criado com sucesso:", response.data);

            setSuccessOrderId(response.data.pedidoId);

        } catch (error: any) {
            console.error("Erro ao salvar pedido:", error);
            setErrorAlert(`Erro ao salvar pedido: ${error.response?.data?.error || error.message}`);
        } finally {
            setSaving(false);
        }
    };

    const resetForm = () => {
        setStep(1);
        setActiveTab('ENTREGA');
        setReviewTab('ITENS');
        setShowItemModal(false);
        setEditingItem(null);
        setFormData({
            OPERACAO: '',
            COND_PAGAMENTO: '',
            COND_PAGAMENTO_ID: '',
            PRAZO_MEDIO: '',
            PRAZO: '',
            EMISSAO: new Date().toISOString().split('T')[0],
            PREVISAO: '',
            ORDEM_COMPRA: '',
            OBS: '',
            CONTATO: '',
            OBSERVACOES_PCP: '',
            OBSERVACOES_FATURAMENTO: ''
        });
        setSelectedClient(null);
        setTaxInfo(null);
        setPddTax(0);
        setFinancialTax(0);
        setSelectedFiscalOp(null);
        setItems([]);
        setSaving(false);
    };

    const goToStep2 = () => {
        if (!selectedClient || !formData.OPERACAO || !formData.COND_PAGAMENTO) {
            setErrorAlert("Preencha todos os campos do cabeçalho antes de prosseguir.");
            return;
        }
        setStep(2);
    };

    // --- TOTALS ---
    const totalValor = items.reduce((acc, item) => acc + item.valor_total, 0);
    const totalItens = items.reduce((acc, item) => acc + item.quantidade, 0);
    const totalFinal = totalValor;

    // --- CÁLCULO DE SIMULAÇÃO DE META (idêntico ao ModernGoalWidget) ---
    // Usa scoreFinal do backend como âncora e aplica delta proporcional,
    // respeitando pesos de margem específicos de cada vendedor.
    const currentGoalPct = React.useMemo(() => {
        if (!goalSimulation || goalSimulation.target <= 0) return 0;
        const sf = Number(goalSimulation.scoreFinal) || 0;
        return sf; // score real já ponderado pelo backend
    }, [goalSimulation]);

    const newGoalPct = React.useMemo(() => {
        if (!goalSimulation || goalSimulation.target <= 0) return 0;
        const target        = Number(goalSimulation.target)        || 0;
        const current       = Number(goalSimulation.current)       || 0;
        const scoreFinal    = Number(goalSimulation.scoreFinal)    || 0;
        const marginGoal    = Number(goalSimulation.marginGoal)    || 0;
        const currentMargin = Number(goalSimulation.currentMargin) || 0;
        const newSales = current + totalFinal;

        // Score BASE com valores atuais (mesma fórmula do ModernGoalWidget)
        let scoreBase: number;
        if (marginGoal > 0) {
            const avBase = Math.min(current       / target,      2);
            const amBase = Math.min(currentMargin / marginGoal,  2);
            scoreBase = (avBase * 0.5 + amBase * 0.5) * 100;
        } else {
            scoreBase = Math.min((current / target) * 100, 200);
        }

        // Score COM pedido (margem não muda, só vendas)
        let scoreComSim: number;
        if (marginGoal > 0) {
            const avSim = Math.min(newSales       / target,      2);
            const amSim = Math.min(currentMargin  / marginGoal,  2); // margem inalterada
            scoreComSim = Math.min((avSim * 0.5 + amSim * 0.5) * 100, 200);
        } else {
            scoreComSim = Math.min((newSales / target) * 100, 200);
        }

        // Delta aplicado sobre o scoreFinal real do backend
        const delta = scoreComSim - scoreBase;
        return Math.min(scoreFinal + delta, 200);
    }, [goalSimulation, totalFinal]);

    const impactPct = (Number(newGoalPct) || 0) - (Number(currentGoalPct) || 0);

    const totalOrderVMargem = items.reduce((acc, item) => {
        const price = item.preco || 0;
        const qty = item.quantidade || 0;
        const icmsRate = Number(taxInfo?.icms) || 0;
        const pisCofinsRate = Number(selectedFiscalOp?.PIS_COFINS) || 0;
        const cprbRate = Number(selectedFiscalOp?.CONT_PREV) || 0;
        const isExportClient = taxInfo?.segmentoId === 10;
        const isImbiseg = ((item.grupo2 || '').toUpperCase() === 'IMBISEG' || (item.linha || '').toUpperCase().includes('IMBISEG'));
        const isImbisegExport = isImbiseg && isExportClient;
        const pddRate = isImbisegExport ? 0 : (Number(pddTax) || 0);
        const finRate = Number(financialTax) || 0;
        const freteRate = Number(taxInfo?.frete) || 0;
        const comRate = Number(item.comissao) || 0;
        const fCom1 = isImbisegExport ? 0 : comRate;

        const vCost = (item.base_cost + item.option_cost) * qty;
        const vImpostos = (price * qty) * ((icmsRate + pisCofinsRate + cprbRate) / 100);
        const vDespesas = (price * qty) * ((pddRate + finRate + freteRate + fCom1) / 100);
        const vMargem = (price * qty) - vCost - vImpostos - vDespesas;

        return acc + vMargem;
    }, 0);

    const totalOrderPMargem = totalFinal > 0 ? (totalOrderVMargem / totalFinal) * 100 : 0;

    const totalValorStandard = items.reduce((acc, item) => {
        const isImbiseg = item.grupo2?.toUpperCase() === 'IMBISEG' || item.linha?.toUpperCase().includes('IMBISEG');
        return isImbiseg ? acc : acc + item.valor_total;
    }, 0);

    const totalValorImbiseg = totalValor - totalValorStandard;

    // Display values (usado para cálculo de deduções)
    const freteRate = taxInfo?.frete || 0;
    const pisCofinsRate = selectedFiscalOp?.PIS_COFINS || 0;
    const cprbRate = selectedFiscalOp?.CONT_PREV || 0;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContainer}>
                <div className={styles.highlightSection}>
                    <h2 className={styles.title}>
                        Novo Pedido
                        <div style={{ display: 'flex', gap: '6px', marginLeft: '16px', alignItems: 'center' }}>
                            {[1, 2, 3].map(s => (
                                <div key={s} style={{
                                    width: step === s ? '24px' : '10px', height: '10px', borderRadius: '10px',
                                    backgroundColor: step >= s ? '#004a91' : '#cbd5e1',
                                    transition: 'all 0.3s ease'
                                }} title={`Etapa ${s}`} />
                            ))}
                        </div>
                    </h2>
                    <button className={styles.closeButton} onClick={onClose} title="Fechar (Esc)">&times;</button>
                </div>

                <div className={styles.contentSection}>

                    {/* --- STEP 1: HEADER --- */}
                    {step === 1 && (
                        <div className={styles.formSection}>
                            <div className={styles.sectionHeader}>
                                <h3 className={styles.sectionTitle}>
                                    <span className={styles.titleAccent}></span>
                                    Dados do Pedido
                                </h3>
                            </div>
                            <div className={`${styles.gridContainer} ${styles.headerGrid}`}>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Cliente *</label>
                                    <ClientSearchInput
                                        onSelect={handleClientSelect}
                                        initialValue={selectedClient?.NOME || ''}
                                    />
                                </div>
                                <div className={styles.formGroup} style={{ position: 'relative' }} ref={opSelectRef}>
                                    <label className={styles.formLabel}>Operação *</label>
                                    <input
                                        type="text"
                                        className={styles.formInput}
                                        placeholder="Selecione ou busque..."
                                        value={isOpSelectOpen ? opSearchTerm : (formData.OPERACAO ? `${selectedFiscalOp?.OPERACAO || ''} - ${formData.OPERACAO}` : '')}
                                        onChange={(e) => {
                                            setOpSearchTerm(e.target.value);
                                            setIsOpSelectOpen(true);
                                        }}
                                        onClick={() => setIsOpSelectOpen(true)}
                                        onKeyDown={handleOpKeyDown}
                                    />
                                    {isOpSelectOpen && ReactDOM.createPortal(
                                        <div className={styles.opDropdown} style={{ ...opDropdownStyle, maxHeight: '250px', overflowY: 'auto' }} ref={opListRef}>
                                            {fiscalOperations
                                                .filter(op => op.DESCRICAO.toLowerCase().includes(opSearchTerm.toLowerCase()) || String(op.OPERACAO).includes(opSearchTerm))
                                                .map((op, index) => (
                                                    <div
                                                        key={op.ID}
                                                        className={styles.opItem}
                                                        onMouseDown={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            setSelectedFiscalOp(op);
                                                            setFormData(prev => ({ ...prev, OPERACAO: op.DESCRICAO }));
                                                            setOpSearchTerm('');
                                                            setIsOpSelectOpen(false);
                                                        }}
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            setSelectedFiscalOp(op);
                                                            setFormData(prev => ({ ...prev, OPERACAO: op.DESCRICAO }));
                                                            setOpSearchTerm('');
                                                            setIsOpSelectOpen(false);
                                                        }}
                                                        style={{ backgroundColor: index === opSelectedIndex ? '#e6f4ff' : undefined }}
                                                    >
                                                        {op.OPERACAO} - {op.DESCRICAO}
                                                    </div>
                                                ))}
                                            {fiscalOperations.filter(op => op.DESCRICAO.toLowerCase().includes(opSearchTerm.toLowerCase()) || String(op.OPERACAO).includes(opSearchTerm)).length === 0 && (
                                                <div style={{ padding: '8px 12px', color: '#888', fontSize: '0.85rem' }}>Nenhuma operação encontrada.</div>
                                            )}
                                        </div>,
                                        document.body
                                    )}
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Condição Pagto *</label>
                                    <PaymentConditionSelect onSelect={handlePaymentSelect} initialValue={formData.COND_PAGAMENTO} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Ordem de Compra</label>
                                    <input
                                        type="text"
                                        className={styles.formInput}
                                        name="ORDEM_COMPRA"
                                        value={formData.ORDEM_COMPRA}
                                        onChange={handleInputChange}
                                        placeholder="Opcional"
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Emissão</label>
                                    <input type="date" className={styles.formInput} name="EMISSAO" value={formData.EMISSAO} onChange={handleInputChange} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.formLabel}>Previsão</label>
                                    <input
                                        type="date"
                                        className={styles.formInput}
                                        name="PREVISAO"
                                        value={formData.PREVISAO}
                                        readOnly
                                        
                                        title="Previsão indisponível no momento"
                                    />
                                </div>
                            </div>

                            {/* Sub-Tabs for Step 1 content — sempre visível, bloqueado sem cliente */}
                            <div style={{ marginTop: '15px', borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                <div style={{ display: 'flex', gap: '10px', borderBottom: '1px solid #ddd', marginBottom: '15px' }}>
                                    <button
                                        onClick={() => setActiveTab('ENTREGA')}
                                        style={{
                                            padding: '10px 20px',
                                            background: 'none',
                                            border: 'none',
                                            borderBottom: activeTab === 'ENTREGA' ? '3px solid #004a91' : '3px solid transparent',
                                            fontWeight: activeTab === 'ENTREGA' ? 'bold' : 'normal',
                                            color: activeTab === 'ENTREGA' ? '#004a91' : '#666',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        ENTREGA
                                    </button>
                                    {/* Future tabs can be added here */}
                                </div>

                                {activeTab === 'ENTREGA' && (
                                    <div style={{ flex: 1, overflowY: 'auto', maxHeight: '50vh', paddingRight: '10px', opacity: selectedClient ? 1 : 0.6, pointerEvents: selectedClient ? 'auto' : 'none' }}>
                                        <h3 style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: '#666', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <PersonIcon fontSize="small" /> Identificação do Cliente
                                        </h3>
                                        <div className={styles.gridContainer} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', rowGap: '15px', marginBottom: '25px' }}>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Nome / Razão Social</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.NOME || ''} readOnly />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>CNPJ / CPF</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.DOCUMENTO || ''} readOnly />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Inscrição Estadual</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.IE || ''} readOnly />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Vendedor</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.VENDEDOR_NOME || ''} readOnly />
                                            </div>
                                        </div>

                                        <h3 style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: '#666', textTransform: 'uppercase', fontWeight: 700, borderTop: '1px solid #eee', paddingTop: '20px' }}>
                                            Endereço de Entrega
                                        </h3>
                                        <div className={styles.gridContainer} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', rowGap: '15px', marginBottom: '25px' }}>
                                            <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}>
                                                <label className={styles.formLabel}>Endereço</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.ENDERECO || ''} onChange={(e) => handleClientDataChange('ENDERECO', e.target.value)} />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Número</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.NUMERO || ''} onChange={(e) => handleClientDataChange('NUMERO', e.target.value)} />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Complemento</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.COMPLEMENTO || ''} onChange={(e) => handleClientDataChange('COMPLEMENTO', e.target.value)} />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Bairro</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.BAIRRO || ''} onChange={(e) => handleClientDataChange('BAIRRO', e.target.value)} />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>CEP</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.CEP || ''} onChange={(e) => handleClientDataChange('CEP', e.target.value)} />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Cidade</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.CIDADE || ''} onChange={(e) => handleClientDataChange('CIDADE', e.target.value)} />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>UF</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.ESTADO || ''} onChange={(e) => handleClientDataChange('ESTADO', e.target.value)} />
                                            </div>
                                        </div>

                                        <h3 style={{ margin: '0 0 15px 0', fontSize: '0.9rem', color: '#666', textTransform: 'uppercase', fontWeight: 700, borderTop: '1px solid #eee', paddingTop: '20px' }}>
                                            Logística e Impostos
                                        </h3>
                                        <div className={styles.gridContainer} style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', rowGap: '15px' }}>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Tipo Frete</label>
                                                <CustomSelect
                                                    id="TIPO_FRETE"
                                                    className={styles.formSelect}
                                                    style={{ width: '100%' }}
                                                    value={selectedClient?.TIPO_FRETE || ''}
                                                    onChange={(e) => handleClientDataChange('TIPO_FRETE', e.target.value)}
                                                    options={[
                                                        { value: '', label: 'Selecione...' },
                                                        ...freightTypes.map(ft => ({ value: ft.TIPO_FRETE, label: ft.TIPO_FRETE }))
                                                    ]}
                                                />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Transportadora</label>
                                                <input type="text" className={styles.formInput} value={selectedClient?.TRANSPORTADORA || ''} readOnly title="Definido pela Logística" />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label className={styles.formLabel}>Redespacho</label>
                                                <CustomSelect
                                                    id="REDESPACHO"
                                                    className={styles.formSelect}
                                                    style={{ width: '100%' }}
                                                    value={selectedClient?.REDESPACHO || ''}
                                                    onChange={(e) => handleClientDataChange('REDESPACHO', e.target.value)}
                                                    options={[
                                                        { value: '', label: 'Nenhum' },
                                                        ...transporters.map(t => ({ value: t.NOME, label: t.NOME }))
                                                    ]}
                                                />
                                            </div>
                                            {/* Resumo Fiscal removed as requested */}
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    )}

                    {/* --- STEP 2: ITEMS & REVIEW (BENTO LAYOUT) --- */}
                    {step === 2 && (
                        <>
                        <div className={styles.step2Container}>

                            {/* LEFT COLUMN: ITEMS LIST */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', overflow: 'hidden' }}>

                                {/* Items Card */}
                                <div style={{ flex: 1, backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: '1px solid #eef2f6' }}>
                                    <div style={{ padding: '15px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#333' }}>Itens do Pedido <span style={{ color: '#888', fontSize: '0.9em', fontWeight: 'normal' }}>({items.length})</span></h3>
                                        <button
                                            onClick={handleOpenItemModal}
                                            className={styles.saveButton}
                                            style={{ padding: '8px 20px', fontSize: '0.9rem', borderRadius: '8px', background: '#004a91', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}
                                        >
                                            + Adicionar Item
                                        </button>
                                    </div>

                                    {/* Global Warnings */}
                                    {items.some(i => i.restricoes.includes('Margem Baixa')) && (
                                        <div style={{ padding: '10px 20px', backgroundColor: 'var(--bg-page-dashboard)', color: 'var(--status-red)', borderBottom: '1px solid var(--border-primary)', fontSize: '0.9rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ display: 'flex', alignItems: 'center' }}><WarningIcon fontSize="small" /></span> <strong>Atenção:</strong> Existem itens com preço abaixo da margem mínima permitida.
                                        </div>
                                    )}

                                    <div className={styles.tableContainer} style={{ flex: 1, overflowY: 'auto', overflowX: 'auto' }}>
                                        <table className={styles.itemsTable} style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0' }}>
                                            <thead style={{ position: 'sticky', top: 0, zIndex: 10, background: '#f8f9fa' }}>
                                                <tr>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>ID</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>VendaID</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>Tab</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>Com%</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>C.IMB</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>Unid</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>Cod PCP</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>Ref</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee', width: '200px' }}>Descrição</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>Opcionais</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee', textAlign: 'right' }}>Preço</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee', textAlign: 'center' }}>Qtd</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee', textAlign: 'right' }}>Total</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>Restrições</th>
                                                    <th style={{ padding: '8px', fontSize: '0.75rem', color: '#666', borderBottom: '1px solid #eee' }}>Tab Lib</th>
                                                    <th style={{ padding: '8px', borderBottom: '1px solid #eee' }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.length === 0 ? (
                                                    <tr><td colSpan={16} style={{ textAlign: 'center', padding: '50px', color: '#999' }}>
                                                        <div style={{ marginBottom: '10px', color: '#cbd5e1' }}><InventoryIcon style={{ fontSize: '3rem' }} /></div>
                                                        Nenhum item adicionado.<br />Utilize o botão acima para começar.
                                                    </td></tr>
                                                ) : (
                                                    items.map(item => (
                                                        <tr
                                                            key={item.id}
                                                            onClick={() => handleEditItem(item)}
                                                            style={{ transition: 'background 0.15s', fontSize: '0.8rem', cursor: 'pointer' }}
                                                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0f4ff')}
                                                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '')}
                                                            title="Clique para editar este item"
                                                        >
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.id.toString().slice(-4)}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.vendaId || '-'}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>
                                                                <div onClick={e => e.stopPropagation()}>
                                                                    <CustomSelect
                                                                        id={`tabela-${item.id}`}
                                                                        style={{ minWidth: '80px', padding: '2px', fontSize: '0.8rem', height: '24px' }}
                                                                        value={item.tabela}
                                                                        onChange={(e) => { e.stopPropagation(); handleItemFieldChange(item.id, 'tabela', e.target.value); }}
                                                                        options={[
                                                                            { value: '', label: '...' },
                                                                            ...tabelasList.map(t => ({ value: t.TABELA, label: t.TABELA }))
                                                                        ]}
                                                                    />
                                                                </div>
                                                            </td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.comissao}%</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.com_imb}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.unidade}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.cod_pcp}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', fontWeight: 'bold' }}>{item.referencia}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.descricao}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.opcionais || '-'}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                                                                <input
                                                                    type="text"
                                                                    value={item.preco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    onChange={(e) => {
                                                                        const rawValue = e.target.value.replace(/\D/g, '');
                                                                        const numberValue = rawValue ? Number(rawValue) / 100 : 0;
                                                                        handleItemFieldChange(item.id, 'preco', numberValue);
                                                                    }}
                                                                    style={{
                                                                        textAlign: 'right',
                                                                        fontWeight: 'bold',
                                                                        width: '80px',
                                                                        padding: '2px',
                                                                        border: '1px solid #ddd',
                                                                        borderRadius: '4px'
                                                                    }}
                                                                />
                                                            </td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center', fontWeight: 'bold' }}>{item.quantidade}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'right', fontWeight: 'bold' }}>{item.valor_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', color: 'red' }}>{item.restricoes}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0' }}>{item.tabela_lib}</td>
                                                            <td style={{ padding: '6px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                                                <button
                                                                    className={styles.removeItemButton}
                                                                    onClick={(e) => { e.stopPropagation(); handleRemoveItem(item.id); }}
                                                                    style={{ color: '#aaa', fontSize: '1.1rem', cursor: 'pointer', background: 'none', border: 'none' }}
                                                                >
                                                                    &times;
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT COLUMN: SIDEBAR SUMMARY */}
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px', overflow: 'hidden' }}>

                                {/* 1. Order Details Card */}
                                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #eef2f6', flex: 1, overflowY: 'auto' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                        <h3 style={{ margin: 0, fontSize: '0.9rem', color: '#666', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <AssignmentIcon fontSize="small" /> Resumo do Pedido
                                        </h3>
                                        {goalSimulation && goalSimulation.target > 0 && totalFinal > 0 && role !== 'admin' && (
                                            <div style={{ background: '#e6f4ea', color: '#137333', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: '4px', boxShadow: '0 2px 4px rgba(19, 115, 51, 0.1)' }}>
                                                +{impactPct >= 100 ? impactPct.toFixed(1) : impactPct.toFixed(2)}% na Meta
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ marginBottom: '10px' }}>
                                        <div style={{ fontSize: '0.8rem', color: '#888' }}>Cliente</div>
                                        <div style={{ fontWeight: '700', color: '#333', fontSize: '1rem', wordBreak: 'break-word', lineHeight: '1.2', marginBottom: '4px' }}>{selectedClient?.NOME}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>{selectedClient?.DOCUMENTO} | {selectedClient?.IE}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#666', marginTop: '2px' }}><strong>Vendedor:</strong> {selectedClient?.VENDEDOR_NOME}</div>
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
                                        <div>
                                            <div style={{ fontSize: '0.8rem', color: '#888' }}>Operação</div>
                                            <div style={{ fontWeight: '500', color: '#333', fontSize: '0.85rem' }}>{formData.OPERACAO}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.8rem', color: '#888' }}>Condição</div>
                                            <div style={{ fontWeight: '500', color: '#333', fontSize: '0.85rem' }}>{formData.COND_PAGAMENTO}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.8rem', color: '#888' }}>Emissão</div>
                                            <div style={{ fontWeight: '500', color: '#333', fontSize: '0.85rem' }}>{new Date(formData.EMISSAO).toLocaleDateString()}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.8rem', color: '#888' }}>
                                                {formData.OPERACAO?.toUpperCase().includes('AMOSTRA GR') ? 'Itens / Pés' : 'Itens / Pares'}
                                            </div>
                                            <div style={{ fontWeight: '500', color: '#333', fontSize: '0.85rem' }}>{items.length} / {totalItens}</div>
                                        </div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #eee', paddingTop: '15px', marginBottom: '10px' }}>
                                        <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#666', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <LocationOnIcon fontSize="small" /> Entrega
                                        </h3>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>{selectedClient?.ENDERECO}, {selectedClient?.NUMERO}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>{selectedClient?.BAIRRO} - {selectedClient?.CIDADE}/{selectedClient?.ESTADO}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}>CEP: {selectedClient?.CEP}</div>
                                    </div>

                                    <div style={{ borderTop: '1px solid #eee', paddingTop: '15px' }}>
                                        <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#666', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <LocalShippingIcon fontSize="small" /> Logística
                                        </h3>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}><strong>Frete:</strong> {selectedClient?.TIPO_FRETE}</div>
                                        <div style={{ fontSize: '0.8rem', color: '#666' }}><strong>Transp.:</strong> {selectedClient?.TRANSPORTADORA || '-'}</div>
                                        {selectedClient?.REDESPACHO && <div style={{ fontSize: '0.8rem', color: '#666' }}><strong>Redesp.:</strong> {selectedClient?.REDESPACHO}</div>}
                                    </div>

                                    <div style={{ marginTop: '10px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                                        <h3 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#666', textTransform: 'uppercase', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <EditIcon fontSize="small" /> Ações
                                        </h3>
                                        <button onClick={() => setStep(1)} style={{ width: '100%', padding: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', borderRadius: '8px', cursor: 'pointer', color: '#475569', fontSize: '0.85rem', fontWeight: 600, transition: 'all 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f1f5f9'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#f8fafc'}>
                                            Editar Cabeçalho do Pedido
                                        </button>
                                    </div>
                                </div>

                                {/* 2. Financials Card */}
                                <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '15px', boxShadow: '0 2px 10px rgba(0,0,0,0.03)', border: '1px solid #eef2f6', display: 'flex', flexDirection: 'column' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '15px' }}>
                                        <div style={{ fontSize: '0.9rem', color: '#888', marginBottom: '4px' }}>Total do Pedido</div>
                                        <div style={{ fontSize: '2rem', fontWeight: '800', color: '#333', lineHeight: 1 }}>
                                            {totalFinal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        </div>
                                        {items.length > 0 && (
                                            <div style={{
                                                fontSize: '0.85rem',
                                                color: totalOrderPMargem >= 0 ? '#16a34a' : '#dc2626',
                                                marginTop: '8px',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}>
                                                <span>{totalOrderPMargem >= 0 ? '' : ''}</span> Margem Média: {totalOrderPMargem.toFixed(2)}%
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.cancelButton} onClick={() => setStep(1)}>
                                Voltar
                            </button>
                            <button className={styles.saveButton} onClick={() => setStep(3)}>
                                Revisão Final &rarr;
                            </button>
                        </div>
                    </>
                    )}

                    {/* --- STEP 3: FINAL REVIEW --- */}
                    {step === 3 && (
                        <>
                        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', padding: '20px', background: '#fafafa', gap: '20px', overflowY: 'auto', flex: 1 }}>

                            {/* 1. Header Summary (Modern Dashboard Style) */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                                {/* Identity Card */}
                                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #eef2f6', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                                    <h4 style={{ margin: '0 0 15px 0', fontSize: '0.85rem', color: '#888', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <PersonIcon fontSize="small" /> Cliente
                                    </h4>
                                    <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#333', marginBottom: '5px' }}>{selectedClient?.NOME}</div>
                                    <div style={{ fontSize: '0.9rem', color: '#555' }}>{selectedClient?.DOCUMENTO}</div>
                                    <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#666', display: 'flex', gap: '15px' }}>
                                        <div style={{ marginTop: '15px', color: '#666', fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <LocationOnIcon fontSize="small" style={{ color: '#888' }} /> {selectedClient?.CIDADE} / {selectedClient?.ESTADO}
                                        </div>
                                    </div>
                                </div>

                                {/* Operation & Financial Card */}
                                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #eef2f6', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                                    <h4 style={{ margin: '0 0 15px 0', fontSize: '0.85rem', color: '#888', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <AssignmentIcon fontSize="small" /> Operação & Financeiro
                                    </h4>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                        <div>
                                            <div style={{ fontSize: '0.75rem', color: '#888' }}>Tipo</div>
                                            <div style={{ fontWeight: '600', color: '#333' }}>{formData.OPERACAO}</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '0.75rem', color: '#888' }}>Condição</div>
                                            <div style={{ fontWeight: '600', color: '#333' }}>{formData.COND_PAGAMENTO}</div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', gap: '15px', fontSize: '0.85rem' }}>
                                        <div style={{ background: '#eef2f6', padding: '4px 8px', borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <CalendarTodayIcon fontSize="small" style={{ color: '#888' }} /> Emissão: {new Date(formData.EMISSAO).toLocaleDateString()}
                                        </div>
                                        <div style={{ background: 'var(--bg-page-dashboard)', padding: '4px 8px', borderRadius: '4px', color: 'var(--status-yellow)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <LocalShippingIcon fontSize="small" /> Previsão: {formData.PREVISAO ? new Date(formData.PREVISAO).toLocaleDateString() : 'N/A'}
                                        </div>
                                    </div>
                                    {formData.ORDEM_COMPRA && (
                                        <div style={{ marginTop: '10px', fontSize: '0.85rem', color: '#666', borderTop: '1px dashed #eee', paddingTop: '5px' }}>
                                            <strong>🏷️ Ordem de Compra:</strong> {formData.ORDEM_COMPRA}
                                        </div>
                                    )}
                                </div>

                                {/* Logistics Details Card */}
                                <div style={{ background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #eef2f6', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}>
                                    <h4 style={{ margin: '0 0 15px 0', fontSize: '0.85rem', color: '#888', textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <LocalShippingIcon fontSize="small" /> Logística
                                    </h4>
                                    <div style={{ fontSize: '0.9rem', marginBottom: '8px' }}>
                                        <strong>Endereço:</strong> {selectedClient?.ENDERECO}, {selectedClient?.NUMERO}
                                    </div>
                                    <div style={{ fontSize: '0.9rem', marginBottom: '8px' }}>
                                        <strong>Frete:</strong> {selectedClient?.TIPO_FRETE}
                                    </div>
                                    <div style={{ fontSize: '0.9rem' }}>
                                        <strong>Transportadora:</strong> {selectedClient?.TRANSPORTADORA || '-'}
                                    </div>
                                </div>
                            </div>

                            {/* 2. Items & Observations Tabs */}
                            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <div style={{ display: 'flex', borderBottom: '1px solid #eee', marginBottom: '15px' }}>
                                    <button
                                        onClick={() => setReviewTab('ITENS')}
                                        style={{
                                            padding: '10px 20px',
                                            background: 'none',
                                            border: 'none',
                                            borderBottom: reviewTab === 'ITENS' ? '3px solid #004a91' : '3px solid transparent',
                                            fontWeight: reviewTab === 'ITENS' ? 'bold' : 'normal',
                                            color: reviewTab === 'ITENS' ? '#004a91' : '#666',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <InventoryIcon fontSize="small" /> ITENS DO PEDIDO ({items.length})
                                    </button>
                                    <button
                                        onClick={() => setReviewTab('OBS')}
                                        style={{
                                            padding: '10px 20px',
                                            background: 'none',
                                            border: 'none',
                                            borderBottom: reviewTab === 'OBS' ? '3px solid #004a91' : '3px solid transparent',
                                            fontWeight: reviewTab === 'OBS' ? 'bold' : 'normal',
                                            color: reviewTab === 'OBS' ? '#004a91' : '#666',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <NotesIcon fontSize="small" /> OBSERVAÇÕES
                                    </button>
                                </div>

                                {reviewTab === 'ITENS' && (
                                    <div className={styles.tableContainer} style={{ overflowX: 'auto', flex: 1 }}>
                                        <table className={styles.itemsTable} style={{ width: '100%', fontSize: '0.75rem', borderCollapse: 'collapse' }}>
                                            <thead>
                                                <tr style={{ background: '#f1f1f1' }}>
                                                    <th style={{ padding: '8px' }}>Ref</th>
                                                    <th style={{ padding: '8px' }}>Descrição</th>

                                                    {/* SIZE GRID HEADERS */}
                                                    {availableSizes.map(size => (
                                                        <th key={size} style={{ textAlign: 'center', width: '35px', background: '#e9ecef', border: '1px solid #dee2e6' }}>{size}</th>
                                                    ))}

                                                    <th style={{ textAlign: 'center', padding: '8px' }}>Tab</th>
                                                    <th style={{ textAlign: 'center', padding: '8px' }}>Com%</th>
                                                    <th style={{ textAlign: 'center', padding: '8px' }}>MC%</th>
                                                    <th style={{ textAlign: 'center', padding: '8px' }}>Qtd</th>
                                                    <th style={{ textAlign: 'right', padding: '8px' }}>Preço Unit.</th>
                                                    <th style={{ textAlign: 'right', padding: '8px' }}>Total</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {items.map(item => (
                                                    <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                                                        <td style={{ fontWeight: 'bold', padding: '8px' }}>{item.referencia}</td>
                                                        <td style={{ padding: '8px' }}>
                                                            <div style={{ fontWeight: '500' }}>{item.descricao}</div>
                                                            {item.opcionais && <div style={{ color: '#888', fontSize: '0.7rem' }}>+ {item.opcionais}</div>}
                                                        </td>

                                                        {/* SIZE GRID VALUES */}
                                                        {availableSizes.map(size => (
                                                            <td key={size} style={{
                                                                textAlign: 'center',
                                                                border: '1px solid #eee',
                                                                background: item.sizes[size] ? '#f0f7ff' : 'transparent',
                                                                fontWeight: item.sizes[size] ? 'bold' : 'normal',
                                                                color: item.sizes[size] ? '#004a91' : '#ccc'
                                                            }}>
                                                                {item.sizes[size] || '-'}
                                                            </td>
                                                        ))}

                                                        <td style={{ textAlign: 'center', padding: '8px' }}>{item.tabela}</td>
                                                        <td style={{ textAlign: 'center', padding: '8px' }}>{item.comissao}%</td>
                                                        <td style={{ textAlign: 'center', padding: '8px' }}>{item.mc_rate || 0}%</td>
                                                        <td style={{ textAlign: 'center', fontWeight: 'bold', padding: '8px', background: '#f8f9fa' }}>{item.quantidade}</td>
                                                        <td style={{ textAlign: 'right', padding: '8px' }}>{Number(item.preco).toFixed(2)}</td>
                                                        <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '8px', color: '#004a91' }}>{item.valor_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {reviewTab === 'OBS' && (
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                                        <textarea
                                            className={styles.formInput}
                                            name="OBS"
                                            value={formData.OBS}
                                            onChange={handleInputChange}
                                            placeholder="Digite aqui quaisquer observações relevantes para este pedido..."
                                            style={{ flex: 1, minHeight: '200px', padding: '15px', fontSize: '1rem', lineHeight: '1.5' }}
                                        />
                                    </div>
                                )}
                            </div>

                            {/* 3. Financial Totals */}
                            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                                {/* Breakdown of deductions */}
                                {role === 'admin' && (
                                    <div style={{ marginBottom: '20px', paddingBottom: '20px', borderBottom: '1px solid #eee' }}>
                                        <div style={{ fontSize: '0.85rem', color: '#888', textTransform: 'uppercase', fontWeight: 'bold', marginBottom: '10px' }}>Detalhamento de Valores</div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', fontSize: '0.85rem' }}>
                                            <div><strong>Valor Bruto:</strong> {totalValor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                                            <div><strong>Frete ({freteRate}%):</strong> {(totalValorStandard * (freteRate / 100)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                                            <div><strong>PDD ({pddTax}%):</strong> {(totalValorStandard * (pddTax / 100)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                                            <div><strong>PIS/COFINS ({pisCofinsRate}%):</strong> {(totalValorStandard * (pisCofinsRate / 100)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                                            <div><strong>CPRB ({cprbRate}%):</strong> {(totalValorStandard * (cprbRate / 100)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}</div>
                                        </div>
                                    </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '30px', background: '#f8fafc', padding: '20px', borderRadius: '12px', border: '1px solid #cbd5e1' }}>
                                    
                                    {goalSimulation && goalSimulation.target > 0 && role !== 'admin' && (
                                        <>
                                            <div style={{ textAlign: 'left', flex: 1 }}>
                                                <div style={{ fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', fontWeight: 600, marginBottom: '5px' }}>Simulação de Meta</div>
                                                <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
                                                    <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#334155' }}>{currentGoalPct >= 100 ? currentGoalPct.toFixed(1) : currentGoalPct.toFixed(2)}%</span>
                                                    <span style={{ color: '#64748b' }}>➔</span>
                                                    <span style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#10b981' }}>{newGoalPct >= 100 ? newGoalPct.toFixed(1) : newGoalPct.toFixed(2)}%</span>
                                                    <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: '#137333', background: '#e6f4ea', padding: '2px 6px', borderRadius: '4px' }}>+{impactPct >= 100 ? impactPct.toFixed(1) : impactPct.toFixed(2)}%</span>
                                                </div>
                                                <div style={{ fontSize: '0.75rem', color: '#475569', fontWeight: 500, marginTop: '4px' }}>Meta Atual vs Com este pedido</div>
                                            </div>
                                            <div style={{ width: '1px', height: '40px', background: '#cbd5e1' }} />
                                        </>
                                    )}

                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', fontWeight: 600, marginBottom: '5px' }}>{formData.OPERACAO?.toUpperCase().includes('AMOSTRA GR') ? 'Pés Totais' : 'Pares Totais'}</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1e293b', lineHeight: 1 }}>{totalItens}</div>
                                    </div>

                                    <div style={{ width: '1px', height: '40px', background: '#cbd5e1' }} />

                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', fontWeight: 600, marginBottom: '5px' }}>Margem R$</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: totalOrderVMargem >= 0 ? '#16a34a' : '#dc2626', lineHeight: 1 }}>
                                            {totalOrderVMargem.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        </div>
                                    </div>

                                    <div style={{ width: '1px', height: '40px', background: '#cbd5e1' }} />

                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', fontWeight: 600, marginBottom: '5px' }}>Margem Média</div>
                                        <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: totalOrderPMargem >= 0 ? '#16a34a' : '#dc2626', lineHeight: 1 }}>
                                            {totalOrderPMargem.toFixed(2)}%
                                        </div>
                                    </div>

                                    <div style={{ width: '1px', height: '40px', background: '#cbd5e1' }} />

                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '0.85rem', color: '#475569', textTransform: 'uppercase', fontWeight: 600, marginBottom: '5px' }}>Valor Total do Pedido</div>
                                        <div style={{ fontSize: '2.2rem', fontWeight: '800', color: '#1e293b', lineHeight: 1 }}>
                                            {totalFinal.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className={styles.modalFooter}>
                            <button className={styles.cancelButton} onClick={() => setStep(2)}>
                                Voltar para Itens
                            </button>
                            <button
                                className={styles.saveButton}
                                onClick={handleSaveOrder}
                                disabled={saving}
                                style={{ background: '#004a91' }}
                            >
                                {saving ? 'Salvando...' : 'Confirmar e Salvar Pedido'}
                            </button>
                        </div>
                    </>
                    )}

                    {/* --- SUB-MODAL: ITEM EDITING --- */}
                    {showItemModal && editingItem && (
                        <div style={{
                            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1100,
                            display: 'flex', justifyContent: 'center', alignItems: 'center'
                        }}>
                            <div style={{
                                backgroundColor: 'white', borderRadius: '8px', width: '1600px', maxWidth: '99%',
                                maxHeight: '97vh', overflowY: 'auto', padding: '30px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)'
                            }}>
                                <h3 style={{ marginTop: 0, borderBottom: '1px solid #eee', paddingBottom: '10px' }}>Adicionar Item</h3>

                                <div style={{ marginBottom: '20px' }}>
                                    <label className={styles.formLabel}>Buscar Produto</label>
                                    <ProductSearchInput
                                        key={productSearchKey}
                                        onSelect={handleProductSelectInModal}
                                        segmentoId={taxInfo?.segmentoId}
                                    />
                                </div>

                                <div style={{ display: 'flex', gap: '20px', marginBottom: '20px', alignItems: 'flex-start' }}>
                                    {editingItem.img && (
                                        <div style={{ flexShrink: 0, width: '100px', display: 'flex', flexDirection: 'column', alignItems: 'center', background: '#f8fafc', padding: '10px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            <img src={editingItem.img} alt="Produto" style={{ width: '100%', height: 'auto', borderRadius: '4px', mixBlendMode: 'multiply' }} />
                                            {editingItem.ca && (
                                                <div style={{ marginTop: '12px', fontSize: '0.85rem', fontWeight: 'bold', color: '#0f172a', background: '#e2e8f0', padding: '4px 10px', borderRadius: '12px' }}>
                                                    CA: {editingItem.ca}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px' }}>
                                    <div>
                                        <label className={styles.formLabel}>Ref</label>
                                        <input type="text" className={styles.formInput} value={editingItem.referencia} readOnly style={{ background: '#eee' }} />
                                    </div>
                                    <div style={{ gridColumn: 'span 2' }}>
                                        <label className={styles.formLabel}>Descrição</label>
                                        <input type="text" className={styles.formInput} value={editingItem.descricao} readOnly style={{ background: '#eee' }} />
                                    </div>
                                    <div>
                                        <label className={styles.formLabel}>Opcionais</label>
                                        <CustomSelect
                                            id="opcionais"
                                            className={styles.formSelect}
                                            style={{ width: '100%' }}
                                            value={editingItem.opcionais}
                                            onChange={(e) => handleEditingItemChange('opcionais', e.target.value)}
                                            options={[
                                                { value: '', label: 'Nenhum' },
                                                ...optionsList.map(opt => ({ value: opt.OPCIONAL, label: `${opt.OPCIONAL} (+R$ ${Number(opt.CUSTO).toFixed(2)})` }))
                                            ]}
                                        />
                                    </div>
                                    <div>
                                        <label className={styles.formLabel}>Tabela</label>
                                        <CustomSelect
                                            id="tabela"
                                            className={styles.formSelect}
                                            style={{ width: '100%' }}
                                            value={editingItem.tabela}
                                            onChange={(e) => handleEditingItemChange('tabela', e.target.value)}
                                            options={[
                                                { value: '', label: '...' },
                                                ...tabelasList.map(t => ({ value: t.TABELA, label: t.TABELA }))
                                            ]}
                                        />
                                    </div>
                                    {(() => {
                                        const currentTableIndex = tabelasList.findIndex(t => t.TABELA === editingItem.tabela);
                                        const currentTable = currentTableIndex !== -1 ? tabelasList[currentTableIndex] : null;
                                        const nextTable = currentTableIndex !== -1 && currentTableIndex < tabelasList.length - 1 ? tabelasList[currentTableIndex + 1] : null;

                                        const currentPrice = currentTable ? calculateFinalSellingPrice(editingItem.base_cost, editingItem.option_cost, editingItem.mc_rate, currentTable.COM || 0, editingItem.linha, editingItem.preco_imbiseg, undefined, undefined, editingItem.quantidade, editingItem.imbiseg_pricelist, editingItem.com_imb, editingItem.grupo2) : null;
                                        const nextPrice = nextTable ? calculateFinalSellingPrice(editingItem.base_cost, editingItem.option_cost, editingItem.mc_rate, nextTable.COM || 0, editingItem.linha, editingItem.preco_imbiseg, undefined, undefined, editingItem.quantidade, editingItem.imbiseg_pricelist, editingItem.com_imb, editingItem.grupo2) : null;

                                        return (
                                            <>
                                                {currentTable && (
                                                    <div>
                                                        <label className={styles.formLabel} style={{ color: '#64748b' }}>Tabela 1</label>
                                                        <input type="text" className={styles.formInput} value={currentPrice?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} readOnly style={{ background: '#f1f5f9', color: '#64748b', borderColor: '#e2e8f0', cursor: 'default' }} tabIndex={-1} title={`Preço limite mínimo (refere-se à tabela atual ${currentTable.TABELA})`} />
                                                    </div>
                                                )}
                                                {nextTable && (
                                                    <div>
                                                        <label className={styles.formLabel} style={{ color: '#64748b' }}>Tabela 2</label>
                                                        <input type="text" className={styles.formInput} value={nextPrice?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} readOnly style={{ background: '#f1f5f9', color: '#64748b', borderColor: '#e2e8f0', cursor: 'default' }} tabIndex={-1} title={`Preço limite máximo (refere-se à próxima tabela ${nextTable.TABELA})`} />
                                                    </div>
                                                )}
                                            </>
                                        );
                                    })()}

                                    {(editingItem.grupo2?.toUpperCase() === 'IMBISEG' || editingItem.linha?.toUpperCase().includes('IMBISEG')) && (
                                        <div>
                                            <label className={styles.formLabel} style={{ color: '#007bff', fontWeight: 'bold' }}>Faixa IMBISEG %</label>
                                            {editingItem.imbiseg_pricelist && editingItem.imbiseg_pricelist.length > 0 ? (
                                                <CustomSelect
                                                    id="com_imb"
                                                    className={styles.formSelect}
                                                    style={{ width: '100%', border: '2px solid #007bff', borderRadius: '6px' }}
                                                    value={editingItem.com_imb}
                                                    onChange={(e) => handleEditingItemChange('com_imb', parseFloat(e.target.value))}
                                                    options={(() => {
                                                        const unique: number[] = [];
                                                        editingItem.imbiseg_pricelist.forEach(p => {
                                                            const c = parseFloat(String(p.comissao));
                                                            if (unique.indexOf(c) === -1) {
                                                                unique.push(c);
                                                            }
                                                        });
                                                        return unique.sort((a, b) => a - b).map(c => ({
                                                            value: c * 100,
                                                            label: `${(c * 100).toFixed(1)}%`
                                                        }));
                                                    })()}
                                                />
                                            ) : (
                                                <input type="text" className={styles.formInput} value="Nenhuma Tabela" readOnly />
                                            )}
                                        </div>
                                    )}

                                    {!(editingItem.grupo2?.toUpperCase() === 'IMBISEG' || editingItem.linha?.toUpperCase().includes('IMBISEG')) && role === 'admin' && (
                                        <div>
                                            <label className={styles.formLabel}>Comissão Vendedor %</label>
                                            <input
                                                type="number"
                                                className={styles.formInput}
                                                value={editingItem.comissao}
                                                step="0.1"
                                                onChange={(e) => handleEditingItemChange('comissao', parseFloat(e.target.value))}
                                            />
                                        </div>
                                    )}

                                        </div>
                                    </div>
                                </div>

                                {/* Floating plain text MC MÍNIMA above the grid container */}
                                {!((editingItem.grupo2 || '').toUpperCase() === 'IMBISEG' || (editingItem.linha || '').toUpperCase().includes('IMBISEG')) && (
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0px', marginBottom: '5px', paddingRight: '5px' }}>
                                        <span style={{ fontSize: '0.95rem', color: '#666', fontWeight: '500' }}>
                                            MC MÍNIMA: <strong style={{ color: '#000', fontSize: '1.2rem', marginLeft: '6px' }}>{(editingItem.mc_rate || 0).toFixed(2)}%</strong>
                                        </span>
                                    </div>
                                )}

                                {/* Grid layout with inline QTDE, Price and Total */}
                                <div style={{ marginTop: '10px', marginBottom: '20px', background: '#f8f9fa', padding: '15px', borderRadius: '6px', border: '1px solid #e9ecef' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                            <label className={styles.formLabel} style={{ margin: 0, fontSize: '0.9rem', color: '#666', fontWeight: 'bold' }}>
                                                {formData.OPERACAO?.toUpperCase().includes('AMOSTRA GR') ? 'Grade (Pés)' : 'Grade (Pares)'}
                                            </label>
                                            {!editingItem.referencia && (
                                                <span style={{ fontSize: '0.85rem', color: '#ef4444', fontWeight: '500' }}>
                                                    * Selecione um produto para habilitar o preenchimento da grade.
                                                </span>
                                            )}
                                        </div>
                                        {/* Margens inline no header */}
                                        {(() => {
                                            const price = editingItem.preco || 0;
                                            const qty = editingItem.quantidade || 0;
                                            const icmsRate = Number(taxInfo?.icms) || 0;
                                            const pisCofinsRate = Number(selectedFiscalOp?.PIS_COFINS) || 0;
                                            const cprbRate = Number(selectedFiscalOp?.CONT_PREV) || 0;
                                            const isExportClient = taxInfo?.segmentoId === 10;
                                            const isImbiseg = ((editingItem.grupo2 || '').toUpperCase() === 'IMBISEG' || (editingItem.linha || '').toUpperCase().includes('IMBISEG'));
                                            const isImbisegExport = isImbiseg && isExportClient;
                                            const pddRate = isImbisegExport ? 0 : (Number(pddTax) || 0);
                                            const finRate = Number(financialTax) || 0;
                                            const freteRate = Number(taxInfo?.frete) || 0;
                                            const comRate = Number(editingItem.comissao) || 0;
                                            const fCom1 = isImbisegExport ? 0 : comRate;
                                            const vCost = (editingItem.base_cost + editingItem.option_cost) * qty;
                                            const vImpostos = (price * qty) * ((icmsRate + pisCofinsRate + cprbRate) / 100);
                                            const vDespesas = (price * qty) * ((pddRate + finRate + freteRate + fCom1) / 100);
                                            const vMargem = (price * qty) - vCost - vImpostos - vDespesas;
                                            const pMargem = (price * qty) > 0 ? (vMargem / (price * qty)) * 100 : 0;
                                            const margemColor = pMargem >= (editingItem.mc_rate || 0) ? '#16a34a' : '#dc2626';
                                            return (
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Margem R$</div>
                                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: margemColor }}>
                                                            {vMargem.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                        </div>
                                                    </div>
                                                    <div style={{ width: '1px', height: '32px', backgroundColor: '#e2e8f0' }}></div>
                                                    <div style={{ textAlign: 'right' }}>
                                                        <div style={{ fontSize: '0.68rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>% Margem Atual</div>
                                                        <div style={{ fontSize: '1rem', fontWeight: 700, color: margemColor }}>
                                                            {pMargem.toFixed(2)}%
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                    <div style={{ display: 'flex', flexWrap: 'nowrap', gap: '15px', alignItems: 'flex-end', paddingBottom: '10px' }}>

                                        {/* Sizes Grid */}
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            {availableSizes.map(size => (
                                                <div key={size} style={{ textAlign: 'center', minWidth: '45px' }}>
                                                    <span style={{ fontSize: '0.9rem', color: '#333', fontWeight: 'bold' }}>{size}</span>
                                                    <input
                                                        type="text"
                                                        value={editingItem.sizes[size] || ''}
                                                        disabled={!editingItem.referencia}
                                                        onChange={(e) => {
                                                            const val = e.target.value.replace(/[^0-9]/g, '');
                                                            handleSizeChangeInModal(size, val ? parseInt(val) : 0);
                                                        }}
                                                        style={{
                                                            width: '100%', padding: '8px 0', textAlign: 'center', border: '1px solid #ccc', borderRadius: '4px',
                                                            backgroundColor: !editingItem.referencia ? '#fde8e8' : (editingItem.sizes[size] ? '#e8f0fe' : 'white'),
                                                            cursor: !editingItem.referencia ? 'not-allowed' : 'text',
                                                            marginTop: '5px',
                                                            fontWeight: 'bold',
                                                            color: !editingItem.referencia ? '#ef4444' : '#000'
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>

                                        {/* Separator */}
                                        <div style={{ width: '1px', height: '40px', backgroundColor: '#ddd', margin: '0 5px' }}></div>

                                        {/* QTDE */}
                                        <div style={{ textAlign: 'center', minWidth: '70px' }}>
                                            <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: 'bold' }}>QTDE</span>
                                            <input
                                                type="text"
                                                value={editingItem.quantidade}
                                                readOnly
                                                style={{
                                                    width: '100%', padding: '8px 0', textAlign: 'center', border: '1px solid #ccc', borderRadius: '4px',
                                                    backgroundColor: '#f5f5f5', marginTop: '5px', fontWeight: 'bold', color: '#333', fontSize: '1.15rem'
                                                }}
                                            />
                                        </div>

                                        {/* $ PREÇO */}
                                        <div style={{ textAlign: 'center', minWidth: '110px' }}>
                                            <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: 'bold' }}>$ PREÇO</span>
                                            <input
                                                type="text"
                                                value={editingItem.preco ? editingItem.preco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0,00'}
                                                onChange={(e) => {
                                                    const rawValue = e.target.value.replace(/\D/g, '');
                                                    const numberValue = rawValue ? Number(rawValue) / 100 : 0;
                                                    handleEditingItemChange('preco', numberValue);
                                                }}
                                                disabled={!editingItem.referencia}
                                                style={{
                                                    width: '100%', padding: '8px 10px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px',
                                                    backgroundColor: 'white', marginTop: '5px', fontWeight: 'bold', color: '#004a91', fontSize: '1.15rem'
                                                }}
                                            />
                                        </div>

                                        {/* $ TOTAL */}
                                        <div style={{ textAlign: 'center', minWidth: '130px' }}>
                                            <span style={{ fontSize: '0.9rem', color: '#666', fontWeight: 'bold' }}>$ TOTAL</span>
                                            <input
                                                type="text"
                                                value={editingItem.valor_total.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                readOnly
                                                style={{
                                                    width: '100%', padding: '8px 10px', textAlign: 'right', border: '1px solid #ccc', borderRadius: '4px',
                                                    backgroundColor: '#f5f5f5', marginTop: '5px', fontWeight: 'bold', color: '#333', fontSize: '1.15rem'
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Botão abaixo da grade */}
                                {editingItem.referencia && (
                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px', marginBottom: '4px' }}>
                                        <button
                                            onClick={handleAddGrade}
                                            title="Salvar esta grade e adicionar outra"
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: '6px',
                                                padding: '5px 14px', borderRadius: '20px',
                                                border: '1.5px solid #004a91',
                                                background: 'white', color: '#004a91',
                                                fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
                                                transition: 'all 0.15s'
                                            }}
                                            onMouseEnter={e => { e.currentTarget.style.background = '#004a91'; e.currentTarget.style.color = 'white'; }}
                                            onMouseLeave={e => { e.currentTarget.style.background = 'white'; e.currentTarget.style.color = '#004a91'; }}
                                        >
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                                                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                                            </svg>
                                            Salvar esta grade e adicionar outra
                                        </button>
                                    </div>
                                )}

                                {editingItem.restricoes && (
                                    <div style={{ color: 'red', fontWeight: 'bold', marginBottom: '15px', textAlign: 'right' }}>
                                        <WarningIcon fontSize="small" style={{ marginRight: '4px' }} /> {editingItem.restricoes}
                                    </div>
                                )}


                                {/* Lista de grades pendentes — tabela estilo etapa 3 */}
                                {pendingItems.length > 0 && (
                                    <div style={{ marginTop: '16px', borderTop: '2px dashed #e2e8f0', paddingTop: '12px' }}>
                                        <div style={{ marginBottom: '8px' }}>
                                            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                Grades adicionadas ({pendingItems.length})
                                            </div>
                                        </div>
                                        <div style={{ overflowX: 'auto', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                                            <div style={{ maxHeight: '270px', overflowY: 'auto' }}>
                                                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                                                    <thead style={{ position: 'sticky', top: 0, zIndex: 5 }}>
                                                        <tr style={{ background: '#f1f1f1' }}>
                                                            <th style={{ padding: '8px', fontSize: '0.72rem', color: '#666', borderBottom: '1px solid #dee2e6', whiteSpace: 'nowrap', textAlign: 'left' }}>Ref</th>
                                                            <th style={{ padding: '8px', fontSize: '0.72rem', color: '#666', borderBottom: '1px solid #dee2e6', textAlign: 'left', minWidth: '180px' }}>Descrição</th>
                                                            {availableSizes.map(s => (
                                                                <th key={s} style={{ padding: '8px 4px', fontSize: '0.72rem', color: '#333', borderBottom: '1px solid #dee2e6', borderLeft: '1px solid #dee2e6', borderRight: '1px solid #dee2e6', textAlign: 'center', minWidth: '32px', background: '#e9ecef' }}>{s}</th>
                                                            ))}
                                                            <th style={{ padding: '8px', fontSize: '0.72rem', color: '#666', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>Tab</th>
                                                            <th style={{ padding: '8px', fontSize: '0.72rem', color: '#666', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>MC%</th>
                                                            <th style={{ padding: '8px', fontSize: '0.72rem', color: '#666', borderBottom: '1px solid #dee2e6', textAlign: 'center' }}>Qtd</th>
                                                            <th style={{ padding: '8px', fontSize: '0.72rem', color: '#666', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>Preço Unit.</th>
                                                            <th style={{ padding: '8px', fontSize: '0.72rem', color: '#666', borderBottom: '1px solid #dee2e6', textAlign: 'right' }}>Total</th>
                                                            <th style={{ padding: '8px', borderBottom: '1px solid #dee2e6' }}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {pendingItems.map((pi, idx) => (
                                                            <tr key={pi.id}
                                                                style={{
                                                                    borderBottom: '1px solid #eee',
                                                                    transition: 'background 0.1s',
                                                                    cursor: 'pointer',
                                                                    background: editingPendingIdx === idx ? 'var(--bg-page-dashboard)' : undefined,
                                                                    outline: editingPendingIdx === idx ? '2px solid #f59e0b' : undefined,
                                                                    opacity: editingPendingIdx === idx ? 0.7 : 1
                                                                }}
                                                                title={editingPendingIdx === idx ? 'Em edição...' : 'Clique para editar esta grade'}
                                                                onClick={() => {
                                                                    // Marca o índice em edição sem remover da lista
                                                                    setEditingItem({ ...pi });
                                                                    setProductSearchKey(prev => prev + 1);
                                                                    setEditingPendingIdx(idx);
                                                                }}
                                                                onMouseEnter={e => { if (editingPendingIdx !== idx) e.currentTarget.style.backgroundColor = '#f0f4ff'; }}
                                                                onMouseLeave={e => { if (editingPendingIdx !== idx) e.currentTarget.style.backgroundColor = ''; }}
                                                            >
                                                                <td style={{ padding: '8px', fontWeight: 'bold', color: '#004a91', whiteSpace: 'nowrap' }}>{pi.referencia}</td>
                                                                <td style={{ padding: '8px', color: '#333', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    <div style={{ fontWeight: '500' }}>{pi.descricao}</div>
                                                                    {pi.opcionais && <div style={{ color: '#888', fontSize: '0.7rem' }}>+ {pi.opcionais}</div>}
                                                                </td>
                                                                {availableSizes.map(s => (
                                                                    <td key={s} style={{
                                                                        padding: '8px 4px',
                                                                        textAlign: 'center',
                                                                        borderLeft: '1px solid #eee',
                                                                        borderRight: '1px solid #eee',
                                                                        background: pi.sizes[s] ? '#f0f7ff' : 'transparent',
                                                                        fontWeight: pi.sizes[s] ? 'bold' : 'normal',
                                                                        color: pi.sizes[s] ? '#004a91' : '#ccc'
                                                                    }}>
                                                                        {pi.sizes[s] || '-'}
                                                                    </td>
                                                                ))}
                                                                <td style={{ padding: '8px', textAlign: 'center', color: '#555' }}>{pi.tabela || '-'}</td>
                                                                <td style={{ padding: '8px', textAlign: 'center', color: '#555' }}>{pi.mc_rate ? `${pi.mc_rate}%` : '0%'}</td>
                                                                <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold', background: '#f8f9fa' }}>{pi.quantidade}</td>
                                                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{pi.preco.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                                                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold', color: '#004a91' }}>
                                                                    {pi.valor_total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                                </td>
                                                                <td style={{ padding: '8px', textAlign: 'center' }} onClick={e => e.stopPropagation()}>
                                                                    {editingPendingIdx === idx ? (
                                                                        <span style={{ fontSize: '0.7rem', color: '#f59e0b', fontWeight: 'bold' }}>editando</span>
                                                                    ) : (
                                                                        <button
                                                                            onClick={(e) => { e.stopPropagation(); if (editingPendingIdx === idx) setEditingPendingIdx(null); setPendingItems(prev => prev.filter((_, i) => i !== idx)); }}
                                                                            title="Remover"
                                                                            style={{ background: 'none', border: 'none', color: '#ccc', fontSize: '1.1rem', cursor: 'pointer', lineHeight: 1, padding: '0', transition: 'color 0.15s' }}
                                                                            onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
                                                                            onMouseLeave={e => e.currentTarget.style.color = '#ccc'}
                                                                        >&times;</button>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}



                                {/* Total parcial + botões de ação */}
                                <div style={{ marginTop: '24px' }}>
                                    {pendingItems.length > 0 && (() => {
                                        const icmsRate = Number(taxInfo?.icms) || 0;
                                        const pisCofinsRate = Number(selectedFiscalOp?.PIS_COFINS) || 0;
                                        const cprbRate = Number(selectedFiscalOp?.CONT_PREV) || 0;
                                        const freteRate = Number(taxInfo?.frete) || 0;
                                        const isExportClient = taxInfo?.segmentoId === 10;

                                        let totalRevenue = 0;
                                        let totalMargem = 0;
                                        let minMcRate = Infinity;

                                        pendingItems.forEach(pi => {
                                            const isImbiseg = ((pi.grupo2 || '').toUpperCase() === 'IMBISEG' || (pi.linha || '').toUpperCase().includes('IMBISEG')) && !isExportClient;
                                            const pddRate = isImbiseg ? 0 : (Number(pddTax) || 0);
                                            const finRate = Number(financialTax) || 0;
                                            const fCom1 = isImbiseg ? 0 : Math.min(3, Number(pi.comissao) || 0);
                                            const qty = pi.quantidade;
                                            const price = Number(pi.preco) || 0;
                                            const vRevenue = price * qty;
                                            const vCost = (pi.base_cost + pi.option_cost) * qty;
                                            const vImpostos = vRevenue * ((icmsRate + pisCofinsRate + cprbRate) / 100);
                                            const vDespesas = vRevenue * ((pddRate + finRate + freteRate + fCom1) / 100);
                                            totalRevenue += vRevenue;
                                            totalMargem += vRevenue - vCost - vImpostos - vDespesas;
                                            if ((pi.mc_rate || 0) < minMcRate) minMcRate = pi.mc_rate || 0;
                                        });

                                        const avgMargin = totalRevenue > 0 ? (totalMargem / totalRevenue) * 100 : 0;
                                        const mcRef = minMcRate === Infinity ? 0 : minMcRate;
                                        const marginColor = avgMargin >= mcRef ? '#16a34a' : avgMargin >= mcRef * 0.9 ? '#d97706' : '#dc2626';
                                        const totalParcial = pendingItems.reduce((acc, pi) => acc + pi.valor_total, 0);

                                        return (
                                            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '24px', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e9ecef' }}>
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Margem Média</div>
                                                    <strong style={{ color: '#555', fontSize: '1rem' }}>
                                                        {avgMargin.toFixed(2)}%
                                                    </strong>
                                                </div>
                                                <div style={{ width: '1px', height: '36px', background: '#e2e8f0' }} />
                                                <div style={{ textAlign: 'right' }}>
                                                    <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '2px' }}>Total Parcial</div>
                                                    <strong style={{ color: '#333', fontSize: '1.6rem' }}>
                                                        {totalParcial.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                                                    </strong>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                        <button className={styles.cancelButton} onClick={() => { setShowItemModal(false); setPendingItems([]); }}>Cancelar</button>
                                        <button className={styles.saveButton} onClick={saveItemFromModal}>
                                            {(() => {
                                                const extraFromCurrent = editingPendingIdx === null && editingItem.quantidade > 0 ? 1 : 0;
                                                const total = pendingItems.length + extraFromCurrent;
                                                return `Confirmar Pedido${total > 0 ? ` (${total} grade${total !== 1 ? 's' : ''})` : ''}`;
                                            })()}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {errorAlert && (
                        <div className={styles.alertDialogOverlay}>
                            <div className={styles.alertDialogBox}>
                                <div className={styles.alertDialogHeader}>
                                    <svg className={styles.alertDialogIconWarning} width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                                    </svg>
                                    <h3 className={styles.alertDialogTitle}>Atenção</h3>
                                </div>
                                <p className={styles.alertDialogBody}>{errorAlert}</p>
                                <div className={styles.alertDialogFooter}>
                                    <button
                                        className={styles.alertDialogPrimaryBtn}
                                        onClick={() => setErrorAlert(null)}
                                    >
                                        OK
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>

                {step === 1 && (
                    <div className={styles.modalFooter}>
                        <button className={styles.cancelButton} onClick={onClose}>
                            Cancelar
                        </button>
                        <button className={styles.saveButton} onClick={goToStep2}>
                            Próximo &rarr;
                        </button>
                    </div>
                )}
            </div>
            {/* --- SUB-MODAL: SUCCESS MESSAGE --- */}
            {successOrderId && (
                <div className={styles.alertDialogOverlay}>
                    <div className={styles.alertDialogBox}>
                        <div className={styles.alertDialogHeader}>
                            <svg className={styles.alertDialogIconSuccess} width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                            </svg>
                            <h3 className={styles.alertDialogTitle}>Sucesso!</h3>
                        </div>
                        <p className={styles.alertDialogBody}>
                            O pedido <strong>#{successOrderId}</strong> foi criado e salvo com sucesso no sistema.
                        </p>
                        <div className={styles.alertDialogFooter}>
                            <button
                                className={styles.alertDialogPrimaryBtn}
                                onClick={() => {
                                    resetForm();
                                    if (onOrderCreated) {
                                        onOrderCreated(Number(successOrderId));
                                    } else {
                                        onClose();
                                    }
                                    setSuccessOrderId(null);
                                }}
                            >
                                Ir para Lista de Pedidos
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateOrderModal;